/**
 * ZIZOV Jewellery Finder  v2.6
 * ──────────────────────────────────────────────────────────────
 * Universal jewellery advisor: Rings · Pendants · Earrings · Bracelets
 * Adaptive quiz — step count varies by type + occasion answer:
 *   Rings+Engagement:  Jewellery → Diamond → Occasion → Shape → Style → Budget (6)
 *   Rings+Wedding:     Jewellery → Diamond → Occasion → Shape → Budget          (5)
 *   Rings+Gift/Daily:  Jewellery → Diamond → Occasion → Budget                  (4)
 *   Pendants:          Jewellery → Diamond → Shape → Style → Budget             (5)
 *   Earrings/Bracelets:Jewellery → Diamond → Style → Budget                     (4)
 *
 *  v2.6 changes (2026-04):
 *  ✓ Strict Shape filter — wrong-shape AND no-shape-evidence products are now
 *    hard-blocked (-99) instead of softly penalised (-2.0). When the user picks
 *    a shape, ONLY products with collection-stamp or title-keyword evidence of
 *    that shape survive. The "alternative shapes leak" issue is fully resolved.
 *  ✓ "Gemstone Rings" / "Gemstone Pendants" Style options removed from the UI
 *    (the dedicated Stone="Gemstone" path supersedes them).
 *  ✓ Bug fix — labgrown-heart-cut-diamond-collection now actually fetched;
 *    previously listed in COLLECTION_META_MAP but missing from CFG.collections,
 *    so LG heart-cut rings never received their shape:'heart' stamp.
 *  ✓ Lazy fetch — collections are now fetched on first jewellery-type click
 *    and filtered to that type + cross-type collections. Cuts ~30% of network
 *    requests for pendant/earring/bracelet flows.
 *  ✓ Per-collection localStorage cache (1h TTL) — repeat visits within the same
 *    hour skip the network entirely.
 *  ✓ Empty-state UX — when strict-shape returns 0 results, an explicit message
 *    plus "Change Shape" / "Change Budget" buttons appear, alongside the
 *    consultation CTA. Same logic for budget-only mismatches.
 *  ✓ Refactored two-pass stamping into a single stampProduct() helper — fewer
 *    moving parts, less order-sensitive, easier to maintain.
 *  ✓ Under-Halo style icon now uses its dedicated SVG variant (was rendering
 *    the regular Halo icon, which made the two visually indistinguishable).
 *  ✓ Tracking — results_shown event now also reports jf_strict_shape_active
 *    and jf_result_count_zero, enabling analytics on strict-filter empty rates.
 *
 * v1.2 deep-audit against 721-product export (2026-03-08) — all known bugs fixed:
 *
 *  CRITICAL FIXES:
 *  ✓ Dead code block removed — old CFG block (292 lines) was floating as expression
 *    statements after the new CFG closed; now cleanly removed
 *  ✓ COLLECTION_META_MAP added — stamps every product with _jfType/_jfDiamond at
 *    fetch time using authoritative collection membership (not tags/product_type)
 *  ✓ startFetch() stamps _jfType/_jfDiamond onto products from COLLECTION_META_MAP
 *  ✓ scoreJewelleryType() rewritten:
 *      — collection match → +4.5, confirmed mismatch → -4.0 (beats full budget score)
 *      — scoreProduct() short-circuits at -99 on confirmed mismatch
 *      — eliminates earring/ring confusion (44/45 earrings incorrectly had "Rings" tag)
 *  ✓ scoreDiamond() added — symmetrical: natural scores +1.5 by absence of lab signals;
 *    collection membership gives ±3.0; keyword fallback ±2.5
 *  ✓ FIELD_WEIGHTS reduced to { title: 3, handle: 2 } — tags removed because Zizov
 *    tags contain ONLY delivery times + category names (zero scoring value)
 *  ✓ buildFields() cleaned — removes product_type (Dutch, unreliable), tags (useless),
 *    variants text; adds metals Set, jfType, jfDiamond
 *  ✓ product_type NOT requested in API fields (bandwidth saving)
 *  ✓ rankProducts() shows top-24 fallback when all questions skipped (was empty)
 *
 *  KEYWORD / CONFIG FIXES:
 *  ✓ Removed 'invisible' from modern style (93/721 products → massive false positives)
 *  ✓ Removed 'horizontal' from bar style (too generic — 'horizontal oval' = shape)
 *  ✓ Removed bare 'drop' from drop style ('teardrop' = pear shape; 'WaterDrop' mismatch)
 *  ✓ Removed 'eternity' from anniversary (ambiguous with wedding)
 *  ✓ Removed 'classic' from everyday occasions (too broad — matches 300+ products)
 *  ✓ Added ' (lg)' keyword for lab-grown (271 products have it — most reliable signal)
 *  ✓ Added 'pearshape' (no dash) — appears in actual titles
 *  ✓ Dutch keywords removed from shape scoring (don't appear in scored title/handle fields)
 *  ✓ gemstone-rings excluded from COLLECTION_META_MAP (contains 2 earrings → cross-type)
 *  ✓ personalised excluded (16 pendants + 4 rings → cross-type collection)
 */

(function () {
  'use strict';

  /* ============================================================
     LABELS — single source of truth for all display strings
     ============================================================ */
  const LABELS = {
    jewelleryType: {
      ring:     'Ring',
      pendant:  'Pendant',
      earrings: 'Earrings',
      bracelet: 'Bracelet'
    },
    diamond: {
      natural:  'Natural Diamond',
      labGrown: 'Lab Grown',
      gemstone: 'Gemstone'
    },
    occasion: {
      engagement: 'Engagement',
      wedding:    'Wedding / Bridal',
      birthday:   'Birthday / Gift',
      everyday:   'Everyday'
    },
    style: {
      // Ring styles (engagement only)
      solitaire:      'Solitaire Rings',
      sidestone:      'Side-Stone Rings',
      halo:           'Halo Rings',
      threeStone:     'Trilogy Rings',
      underHalo:      'Under-Halo Rings',
      toiEtMoi:       'Toi et Moi',
      gemstone:       'Gemstone',
      // Pendant styles
      specialPendant: 'Special Pendants',
      // Earring styles
      stud:           'Classic Studs',
      hoop:           'Hoops',
      drop:           'Dangling Earrings',
      // Bracelet styles
      tennis:         'Tennis',
      bangle:         'Bangle'
    },
    shape: {
      round:    'Round',
      oval:     'Oval',
      emerald:  'Emerald',
      cushion:  'Cushion',
      princess: 'Princess',
      pear:     'Pear',
      marquise: 'Marquise',
      radiant:  'Radiant',
      heart:    'Heart'
    }
  };

  /* All valid values per criterion — used for URL param validation */
  const VALID_VALUES = {
    jewelleryType: new Set([...Object.keys(LABELS.jewelleryType), '__skip']),
    diamond:  new Set([...Object.keys(LABELS.diamond),  '__skip']),
    occasion: new Set([...Object.keys(LABELS.occasion), '__skip']),
    style:    new Set([...Object.keys(LABELS.style),    '__skip']),
    shape:    new Set([...Object.keys(LABELS.shape),    '__skip'])
  };


  /* ============================================================
     CONFIGURATION — deep-audited against 721-product export (2026-03-08)
     521 Rings · 101 Pendants · 45 Earrings · 43 Bracelets
     Metals: "Gold" (yellow) · "Rosé Gold" · "White Gold" (exact Shopify option2 values)
     ============================================================ */
  const CFG = {
    /**
     * COLLECTIONS — fetched for product discovery + shape/style stamping.
     *   Type-exclusive collections listed first (ring → pendant → earring → bracelet).
     *   Style/shape-only collections appended at the end — these may contain products
     *   already seen; the two-pass fetch logic stamps shape/style onto them without
     *   re-adding duplicates to the product array.
     */
    collections: [
      /* ── Rings · Natural ─────────────────────────────────── */
      'natural-diamond-rings',
      'natural-engagement-rings',
      'natural-diamond-solitaire-rings',
      'natural-diamond-halo-rings',
      'natural-diamond-under-halo-rings',
      'natural-diamond-side-stone-rings',
      'natural-diamond-trilogy-rings',
      'natural-diamond-toi-et-moi-rings',
      'natural-diamond-eternity-rings',
      'natural-round-brilliant-cut-diamond-collection',
      'natural-oval-cut-diamond-collection',
      'natural-emerald-cut-diamond-collection',
      'natural-cushion-cut-diamond-collection',
      'natural-pear-cut-diamond-collection',
      'natural-marquise-cut-diamond-collection',
      'natural-heart-cut-diamond-collection',
      'natural-princess-cut-diamond-collection',
      'natural-radiant-cut-diamond-collection',
      /* ── Rings · Lab-grown ───────────────────────────────── */
      'lab-grown-diamond-rings',
      'lab-grown-diamond-solitaire-rings',
      'lab-grown-diamond-halo-rings',
      'lab-grown-diamond-under-halo-rings',
      'lab-grown-diamond-side-stone-rings',
      'lab-grown-diamond-trilogy-rings',
      'lab-grown-diamond-toi-et-moi-rings',
      'lab-grown-diamond-eternity-rings',
      'exclusive-rings-lg',
      'lab-grown-round-brilliant-cut-diamond-collection',
      'lab-grown-oval-cut-diamond-collection',
      'lab-grown-emerald-cut-diamond-collection',
      'labgrown-cushion-cut-diamond-collection',
      'labgrown-pear-cut-diamond-collection',
      'lab-grown-marquise-cut-diamond-collection',
      'labgrown-princess-cut-diamond-collection',
      'labgrown-heart-cut-diamond-collection',
      'lab-grown-radiant-cut-diamond-collection',
      'fancy-colour-lg',
      /* ── Rings · Style / Gemstone ────────────────────────── */
      'cocktail-rings',
      'design-rings',
      'invisible-rings',
      'initial-rings',
      'marry-by-shape',
      'pink-diamond',
      'yellow-diamond',
      'tanzanite',
      'tourmaline',
      'garnet-peridot',
      'semi-precious-gemstone-collection',
      /* ── Pendants · Natural ──────────────────────────────── */
      'natural-diamond-pendants',
      /* ── Pendants · Lab-grown ────────────────────────────── */
      'lab-grown-diamond-pendant-collection',
      /* ── Pendants · Style ────────────────────────────────── */
      'solitaire-pendants',
      'halo-pendants-lg',
      'classic-pendants-lg',
      'design-pendants',
      'special-pendants-lg',
      'daily-diamond',
      'invisible-pendant',
      'initial-pendant',
      /* ── Earrings · Natural ──────────────────────────────── */
      'natural-diamond-earrings',
      /* ── Earrings · Lab-grown ────────────────────────────── */
      'lab-grown-diamond-earrings',
      /* ── Earrings · Style ────────────────────────────────── */
      'studs',
      'studs-lg',
      'hoops',
      'hoops-lg',
      'dangle',
      'dangling-lg',
      'invisible-studs',
      'invisible-earring',
      'statements',
      /* ── Bracelets · Natural ─────────────────────────────── */
      'natural-diamond-bracelets',
      /* ── Bracelets · Lab-grown ───────────────────────────── */
      'lab-grown-diamond-bracelets',
      /* ── Bracelets · Style ───────────────────────────────── */
      'tennis-bracelet',
      'tennis-bracelets-lg',
      'bangle',
      'bangles-lg',
      'invisible-bracelet',
      'champagne-diamond',
      /* ── Style-only collections (shape/style stamps, no new product types) ── */
      /* These are fetched solely to stamp shape/style onto products already      */
      /* in the array. Duplicate products are ignored by the two-pass logic.      */
      'classic-solitaire',      // style: solitaire (rings)
      'halo-solitaire',         // style: halo (rings, natural)
      'halo-solitaire-lg',      // style: halo (rings, lab-grown)
      /* ── Gemstone & fancy colour — type-specific where safe ─────────────── */
      /* Ordered so type-exclusive collections above have already stamped the    */
      /* correct type on shared products before these are reached.               */
      'gemstone-rings',               // 69 rings + 2 earrings (earrings already stamped above)
      'precious-gemstone-collection', // 53R+8P+4E — mixed, no type stamp
      'fancy-diamond-collection',     // 21 rings + 2 bracelets + 1 pendant (others already stamped)
      'green-emerald',                // 24R+7P+1E — mixed, no type stamp
      'blue-sapphire',                // 13 rings + 1 earring (already stamped)
      'red-ruby',                     // 12R+2E+1P — mixed, no type stamp
      'amethyst',                     // 3 rings — missing from prior list
      'aquamarine-paraiba',           // 12 rings — missing from prior list
      'brown-diamond',                // 4 rings — missing from prior list
      'black-diamond',                // 4 rings — missing from prior list
      /* ── Cross-type · MUST be LAST so type stamps from above are never overwritten ── */
      'invisible-diamond-collection', // 60R+17P+15E+2B — no type stamp, complements invisible-* colls
    ],

    perPage:    250,
    maxResults: 24,
    /**
     * product_type is NOT requested — it's unreliable (Dutch values not consistently
     * assigned). Type detection is done via collection membership (_jfType stamp).
     */
    fields: 'id,title,handle,variants,images',

    appointmentUrl: 'https://app.acuityscheduling.com/schedule.php?owner=16861728',
    allJewelleryUrl: '/collections/all',

    STEP_KEYS: {
      1: 'jewelleryType',
      2: 'diamond',
      3: 'occasion',
      4: 'shape',
      5: 'style',
      6: null
    },

    JEWELLERY_TYPE_KEYS: ['ring', 'pendant', 'earrings', 'bracelet'],
    BESPOKE_INDEX: 5,

    /**
     * FIELD_WEIGHTS — used by scoreCriterion for occasion / style / shape.
     * product_type removed (unreliable Dutch values, not used).
     * variants removed (only Metal uses them via dedicated fields.metals Set).
     * tags removed (Zizov tags contain ONLY delivery times + category names
     *   e.g. "Rings", "1 week" — zero scoring value for style/occasion/shape).
     */
    FIELD_WEIGHTS: { title: 3, handle: 2 },

    keywords: {
      /**
       * jewelleryType — used ONLY as keyword fallback when _jfType is not set
       * (rare: products in no type-exclusive collection).
       * Uses ' ring' (space-prefixed) to avoid 'ring' matching inside 'earrings'.
       * Handles use '-ring-' which is NOT present in '-earring-'.
       */
      jewelleryType: {
        ring:     [' ring', '-ring-', 'ring-zizov'],
        pendant:  [' pendant', ' necklace', '-pendant'],
        earrings: [' earring', ' studs', ' hoops', '-earring', '-studs-', '-hoops-'],
        bracelet: [' bracelet', ' bangle', '-bracelet', '-bangle']
      },

      /**
       * diamond — used by scoreDiamond() for fallback when _jfDiamond is not set.
       * Lab-grown signals: ' (lg)' suffix in title (271 products), '-lg-' in handle.
       * Natural: absence of lab signals → assume natural (no explicit "natural" in titles).
       */
      diamond: {
        natural:  [],
        labGrown: [' (lg)', '-lg-', 'lab grown', 'lab-grown', 'labgrown', 'cvd'],
        gemstone: ['sapphire', 'ruby', 'emerald', 'tourmaline', 'tanzanite', 'amethyst',
                   'aquamarine', 'paraiba', 'garnet', 'peridot', 'gemstone',
                   'coloured stone', 'color stone', 'precious stone', 'semi-precious',
                   'pink diamond', 'yellow diamond', 'black diamond', 'brown diamond',
                   'fancy colour', 'fancy color', 'green emerald', 'red ruby', 'blue sapphire']
      },

      /**
       * occasion — calibrated to avoid overly broad terms.
       * Removed: 'eternity' from anniversary (also a wedding keyword → ambiguous).
       * Removed: 'tennis' from anniversary (too specific, unreliable signal).
       * Removed: 'solitaire' from engagement (also everyday/pendant style).
       * Kept: 'initial' in birthday (personalised = common gift category).
       */
      occasion: {
        engagement: ['engagement ring', 'engagement-ring', 'engagement', 'verloving',
                     'proposal', 'aanzoek', 'bridal', 'natural-engagement'],
        wedding:    ['wedding band', 'wedding ring', 'trouwring', 'eternity ring',
                     'eternity band', 'half eternity', 'full eternity',
                     'eternity dot', 'alliance'],
        birthday:   ['birthday', 'verjaardag', 'gift', 'cadeau', 'present',
                     'lucky clover', 'hamsa', 'initial pendant', 'initial ring',
                     'personalised', 'initial necklace', 'cutie dot'],
        everyday:   ['daily diamond', 'daily-diamond', 'daily wear', 'minimalist',
                     'dot ring', 'dot necklace', 'stackable', 'flexi-bangle',
                     'flexi bangle', 'cutie dot', 'bar necklace']
      },

      /**
       * style — cross-category: some keys apply to rings, some to earrings/pendants, some to bracelets.
       * The Liquid template uses data-jf-types to show/hide per type in the UI.
       * Here keywords are used for product scoring regardless of type (intentional —
       * a pendant matched to 'halo' style should score high even if type=pendant).
       *
       * Removed: 'invisible' from modern (93/721 products → massive false positive).
       * Removed: 'horizontal' from bar (too generic — 'horizontal oval' is a shape, not bar).
       * Removed: 'drop' alone from drop style ('teardrop' is a shape; 'drop' alone matches
       *   "WaterDrop" earrings, not a hanging-drop design).
       * Fixed: 'pavé' kept in sidestone (sidestones on band; solitaire WITH pavé = sidestone ring).
       */
      style: {
        // Ring styles (engagement only)
        solitaire:      ['solitaire', 'solitair', 'classic solitaire', 'floating solitaire',
                         'solitaire ring', 'solitaire pendant', 'solitaire bangle',
                         'solitaire flexi', 'fortune solitaire'],
        halo:           ['halo', 'halo ring', 'halo pendant',
                         'double halo', 'halo solitaire', 'halo pavé'],
        threeStone:     ['three stone', 'three-stone', 'trilogy', 'trio',
                         'past present future'],
        sidestone:      ['side stone', 'side-stone', 'sidestone', 'with baguettes',
                         'baguette', 'channel', 'pave solitaire', 'pavé solitaire',
                         'pave ring', 'invisible rectangular'],
        underHalo:      ['under halo', 'under-halo', 'under-halo ring'],
        toiEtMoi:       ['toi et moi', 'toi-et-moi', 'toietmoi', 'two stone'],
        // Gemstone — shared by ring (Gemstone Rings) and pendant (Gemstone Pendants)
        gemstone:       ['gemstone pendant', 'coloured pendant', 'sapphire pendant',
                         'ruby pendant', 'emerald pendant', 'precious pendant',
                         'coloured stone pendant', 'gemstone necklace',
                         'gemstone ring', 'coloured ring', 'semi-precious ring'],
        // Pendant styles
        specialPendant: ['special pendant', 'design pendant', 'invisible pendant',
                         'initial pendant', 'daily diamond', 'cutie pendant'],
        // Earring styles
        stud:           ['stud', 'studs', 'ear stud', 'diamond stud',
                         'prongs round stud', 'prong stud', 'classic studs',
                         'invisible stud', 'round stud', 'detachable jacket'],
        hoop:           ['hoop', 'hoops', 'diamond hoops', 'classic diamond hoops',
                         'big diamond hoops', 'square diamond hoops'],
        drop:           ['dangling earring', 'dangle earring', 'drop earring',
                         'round solitaire dangling', 'drop tennis earring'],
        // Bracelet styles
        tennis:         ['tennis bracelet', 'tennis-bracelet', 'teardrop tennis',
                         'ct teardrop', 'mini tennis', 'flexi tennis', 'degrade tennis',
                         'pear-shape tennis', 'oval cut tennis', 'marquise cut tennis',
                         'emerald tennis', 'ct tennis'],
        bangle:         ['bangle', 'flexi-bangle', 'twinny bangle', 'split bangle',
                         'open bangle', 'three-layer', 'bangle with diamonds',
                         'solitaire bangle', 'rose bangle', 'invisible flexi']
      },



      /**
       * shape — keywords from actual product titles (English throughout).
       * Removed Dutch words — don't appear in scored title/handle fields.
       * Removed 'rectangular' from emerald — ZIZOV invisible rectangular ≠ emerald cut.
       * Removed 'step cut' — doesn't appear in product titles.
       * Added 'teardrop' to pear — teardrop tennis bracelet uses pear-shaped diamonds.
       * Added 'pearshape' (no dash) — appears in titles like "Invisible Pearshape Diamond Ring".
       */
      shape: {
        round:    ['round', 'round brilliant', 'round cut', 'classic round',
                   'round solitaire', 'round halo', 'prongs round', 'round stud',
                   'round pendant'],
        oval:     ['oval', 'oval cut', 'oval solitaire', 'oval halo',
                   'horizontal oval', 'classic oval'],
        emerald:  ['emerald cut', 'emerald-cut', 'emerald solitaire',
                   'emerald studs', 'classic emerald', 'ct emerald'],
        cushion:  ['cushion cut', 'cushion-cut', 'cushion solitaire',
                   'cushion halo', 'classic cushion'],
        princess: ['princess cut', 'princess-cut', 'square brilliant',
                   'princess solitaire', 'princess studs'],
        pear:     ['pear-shape', 'pear shape', 'pear cut', 'pearshape',
                   'pear solitaire', 'pear halo', 'pear-shape halo',
                   'classic pear-shape', 'teardrop'],
        marquise: ['marquise', 'marquise cut', 'marquise twinny',
                   'invisible marquise', 'lucky marquise', 'marquise-cut',
                   'marquise solitaire'],
        radiant:  ['radiant cut', 'radiant-cut', 'square radiant',
                   'radiant solitaire', 'classic radiant', 'horizontal radiant',
                   'square radiant studs', 'ct radiant'],
        heart:    ['heart-shape', 'heart shape', 'heart solitaire', 'heart cut',
                   'heart diamond', 'classic heart', 'heart pendant']
      }
    },

    /**
     * Budget ranges — aligned with Shopify smart collection tiers (audit 2026-03-09):
     *   Shopify: natural-under-1-499 / natural-1-499-3-499 / natural-3-499-5-999 / natural-5-999
     *   Same tiers apply to lab-grown smart collections.
     *
     * Dynamic default per jewellery type (set in onCardClick when type is chosen):
     *   ring:     index 1 (€2,000–€4,500) — median €4,384 ✓
     *   pendant:  index 1 (€2,000–€4,500) — median €2,520 ✓
     *   earrings: index 1 (€2,000–€4,500) — median €3,936 ✓
     *   bracelet: index 2 (€4,500–€8,000) — median €6,049 ✓
     *
     * Prices in Shopify API are EUROS (e.g. "5527.00"); × 100 = cents for range comparison.
     */
    budgetRanges: [
      { label: 'Under €2,000',       min: 0,        max: 200000  },
      { label: '€2,000 – €4,500',    min: 200000,   max: 450000  },
      { label: '€4,500 – €8,000',    min: 450000,   max: 800000  },
      { label: '€8,000 – €16,000',   min: 800000,   max: 1600000 },
      { label: '€16,000 – €32,000',  min: 1600000,  max: 3200000 },
      { label: '€32,000+',           min: 3200000,  max: Infinity }
    ],

    /**
     * Default budgetIndex per jewellery type — set dynamically when type is chosen.
     * Falls back to index 1 (€2,000–€4,500) for unknown types.
     */
    budgetDefaultByType: {
      ring:     1,   // median €4,384 → €2,000–€4,500
      pendant:  1,   // median €2,520 → €2,000–€4,500
      earrings: 1,   // median €3,936 → €2,000–€4,500
      bracelet: 2    // median €6,049 → €4,500–€8,000
    }
  };

  /**
   * COLLECTION_META_MAP — maps every collection handle to its jewellery type
   * and (where unambiguous) diamond origin.  Built from the 721-product export.
   *
   * Products are stamped with _jfType / _jfDiamond at fetch time (startFetch).
   * First collection in CFG.collections to yield a product wins (de-duplication),
   * so type-specific collections MUST be listed before cross-category ones.
   *
   * Collections NOT listed here produce products with _jfType = null
   * → those products fall back to keyword-based type detection in scoreJewelleryType.
   *
   * Verified cross-type exceptions (no type stamp to avoid wrong classification):
   *   precious-gemstone-collection → 53R+8P+4E  → type: {} (mixed)
   *   green-emerald               → 24R+7P+1E   → type: {} (mixed)
   *   red-ruby                    → 12R+2E+1P   → type: {} (mixed)
   *   invisible-diamond-collection → 60R+17P+15E+2B → type: {} (cross-type, listed last)
   *   aquamarine-paraiba          → 10R+1P+1B   → excluded entirely (too mixed)
   */
  const COLLECTION_META_MAP = {
    /* ── Rings · Natural ───────────────────────────────────────── */
    'natural-diamond-rings':                        { type: 'ring',     diamond: 'natural'  },
    'natural-engagement-rings':                     { type: 'ring',     diamond: 'natural',  occasion: 'engagement' },
    'natural-diamond-solitaire-rings':              { type: 'ring',     diamond: 'natural',  style: 'solitaire', occasion: 'engagement' },
    'natural-diamond-halo-rings':                   { type: 'ring',     diamond: 'natural',  style: 'halo',      occasion: 'engagement' },
    'natural-diamond-under-halo-rings':             { type: 'ring',     diamond: 'natural',  style: 'underHalo', occasion: 'engagement' },
    'natural-diamond-side-stone-rings':             { type: 'ring',     diamond: 'natural',  style: 'sidestone', occasion: 'engagement' },
    'natural-diamond-trilogy-rings':                { type: 'ring',     diamond: 'natural',  style: 'threeStone', occasion: 'engagement' },
    'natural-diamond-toi-et-moi-rings':             { type: 'ring',     diamond: 'natural',  style: 'toiEtMoi',  occasion: 'engagement' },
    'natural-diamond-eternity-rings':               { type: 'ring',     diamond: 'natural',  occasion: 'wedding' },
    'natural-round-brilliant-cut-diamond-collection': { type: 'ring',   diamond: 'natural',  shape: 'round'    },
    'natural-oval-cut-diamond-collection':          { type: 'ring',     diamond: 'natural',  shape: 'oval'     },
    'natural-emerald-cut-diamond-collection':       { type: 'ring',     diamond: 'natural',  shape: 'emerald'  },
    'natural-cushion-cut-diamond-collection':       { type: 'ring',     diamond: 'natural',  shape: 'cushion'  },
    'natural-pear-cut-diamond-collection':          { type: 'ring',     diamond: 'natural',  shape: 'pear'     },
    'natural-marquise-cut-diamond-collection':      { type: 'ring',     diamond: 'natural',  shape: 'marquise' },
    'natural-heart-cut-diamond-collection':         { type: 'ring',     diamond: 'natural',  shape: 'heart'    },
    'natural-princess-cut-diamond-collection':      { type: 'ring',     diamond: 'natural',  shape: 'princess' },
    'natural-radiant-cut-diamond-collection':       { type: 'ring',     diamond: 'natural',  shape: 'radiant'  },
    /* ── Rings · Lab-grown ─────────────────────────────────────── */
    'lab-grown-diamond-rings':                      { type: 'ring',     diamond: 'labGrown' },
    'lab-grown-diamond-solitaire-rings':            { type: 'ring',     diamond: 'labGrown', style: 'solitaire', occasion: 'engagement' },
    'lab-grown-diamond-halo-rings':                 { type: 'ring',     diamond: 'labGrown', style: 'halo',      occasion: 'engagement' },
    'lab-grown-diamond-under-halo-rings':           { type: 'ring',     diamond: 'labGrown', style: 'underHalo', occasion: 'engagement' },
    'lab-grown-diamond-side-stone-rings':           { type: 'ring',     diamond: 'labGrown', style: 'sidestone', occasion: 'engagement' },
    'lab-grown-diamond-trilogy-rings':              { type: 'ring',     diamond: 'labGrown', style: 'threeStone', occasion: 'engagement' },
    'lab-grown-diamond-toi-et-moi-rings':           { type: 'ring',     diamond: 'labGrown', style: 'toiEtMoi',  occasion: 'engagement' },
    'lab-grown-diamond-eternity-rings':             { type: 'ring',     diamond: 'labGrown', occasion: 'wedding' },
    'exclusive-rings-lg':                           { type: 'ring',     diamond: 'labGrown' },
    'lab-grown-round-brilliant-cut-diamond-collection': { type: 'ring', diamond: 'labGrown', shape: 'round'    },
    'lab-grown-oval-cut-diamond-collection':        { type: 'ring',     diamond: 'labGrown', shape: 'oval'     },
    'lab-grown-emerald-cut-diamond-collection':     { type: 'ring',     diamond: 'labGrown', shape: 'emerald'  },
    'labgrown-cushion-cut-diamond-collection':      { type: 'ring',     diamond: 'labGrown', shape: 'cushion'  },
    'labgrown-pear-cut-diamond-collection':         { type: 'ring',     diamond: 'labGrown', shape: 'pear'     },
    'lab-grown-marquise-cut-diamond-collection':    { type: 'ring',     diamond: 'labGrown', shape: 'marquise' },
    'labgrown-princess-cut-diamond-collection':     { type: 'ring',     diamond: 'labGrown', shape: 'princess' },
    'labgrown-heart-cut-diamond-collection':        { type: 'ring',     diamond: 'labGrown', shape: 'heart'    },
    'lab-grown-radiant-cut-diamond-collection':     { type: 'ring',     diamond: 'labGrown', shape: 'radiant'  },

    /* ── Rings · Style / Gemstone (diamond type unknown) ────────── */
    'cocktail-rings':                    { type: 'ring' },
    'design-rings':                      { type: 'ring' },
    'invisible-rings':                   { type: 'ring' },
    'initial-rings':                     { type: 'ring' },
    'marry-by-shape':                    { type: 'ring' },
    // Gemstone & fancy-colour ring collections — style: 'gemstone' stamps all members
    // so the Gemstone Rings filter returns only gemstone/coloured products.
    // No 'type' on mixed-type collections (green-emerald, red-ruby, precious-gemstone-collection)
    // to avoid mis-stamping the pendants/earrings inside them — those get type from their
    // own type-specific collections (natural-diamond-pendants etc.) which are processed first
    // (alphabetically earlier than 'g'/'p'/'r'/'s').
    'gemstone-rings':                    { style: 'gemstone' },
    'semi-precious-gemstone-collection': { type: 'ring',  style: 'gemstone' },
    'blue-sapphire':                     { style: 'gemstone' },
    'tanzanite':                         { type: 'ring',  style: 'gemstone' },
    'tourmaline':                        { type: 'ring',  style: 'gemstone' },
    'garnet-peridot':                    { type: 'ring',  style: 'gemstone' },
    'pink-diamond':                      { type: 'ring',  style: 'gemstone' },
    'yellow-diamond':                    { type: 'ring',  style: 'gemstone' },
    'fancy-diamond-collection':          { style: 'gemstone' },
    'fancy-colour-lg':                   { type: 'ring',  diamond: 'labGrown', style: 'gemstone' },
    // Mixed-type gemstone colour collections — style only (no type to avoid cross-type errors)
    'precious-gemstone-collection':      { style: 'gemstone' },
    'green-emerald':                     { style: 'gemstone' },
    'red-ruby':                          { style: 'gemstone' },
    'amethyst':                          { style: 'gemstone' },
    'aquamarine-paraiba':                { style: 'gemstone' },
    'brown-diamond':                     { style: 'gemstone' },
    'black-diamond':                     { style: 'gemstone' },
    /* ── Rings · Style-only collections ─────────────────────────── */
    'classic-solitaire':    { type: 'ring', style: 'solitaire' },
    'halo-solitaire':       { type: 'ring', style: 'halo'      },
    'halo-solitaire-lg':    { type: 'ring', diamond: 'labGrown', style: 'halo' },
    /* ── Cross-type · LAST — only stamps products not yet stamped above ── */
    /* invisible-diamond-collection: 60R+17P+15E+2B — complements invisible-* style colls */
    'invisible-diamond-collection':     {},
    /* ── Pendants · Natural ─────────────────────────────────────── */
    'natural-diamond-pendants':             { type: 'pendant',  diamond: 'natural'  },
    /* ── Pendants · Lab-grown ───────────────────────────────────── */
    'lab-grown-diamond-pendant-collection': { type: 'pendant',  diamond: 'labGrown' },
    'halo-pendants-lg':                     { type: 'pendant',  diamond: 'labGrown', style: 'halo'      },
    'classic-pendants-lg':                  { type: 'pendant',  diamond: 'labGrown', style: 'solitaire' },
    'special-pendants-lg':                  { type: 'pendant',  diamond: 'labGrown', style: 'specialPendant' },
    /* ── Pendants · Style (diamond type unknown) ────────────────── */
    'solitaire-pendants':   { type: 'pendant', style: 'solitaire' },
    'design-pendants':      { type: 'pendant', style: 'specialPendant' },
    'daily-diamond':        { type: 'pendant', style: 'specialPendant' },
    'invisible-pendant':    { type: 'pendant', style: 'specialPendant' },
    'initial-pendant':      { type: 'pendant', style: 'specialPendant' },
    /* ── Earrings · Natural ─────────────────────────────────────── */
    'natural-diamond-earrings':     { type: 'earrings', diamond: 'natural'  },
    /* ── Earrings · Lab-grown ───────────────────────────────────── */
    'lab-grown-diamond-earrings':   { type: 'earrings', diamond: 'labGrown' },
    'studs-lg':                     { type: 'earrings', diamond: 'labGrown', style: 'stud'  },
    'hoops-lg':                     { type: 'earrings', diamond: 'labGrown', style: 'hoop'  },
    'dangling-lg':                  { type: 'earrings', diamond: 'labGrown', style: 'drop'  },
    /* ── Earrings · Style (diamond type unknown) ────────────────── */
    'studs':             { type: 'earrings', style: 'stud'  },
    'hoops':             { type: 'earrings', style: 'hoop'  },
    'dangle':            { type: 'earrings', style: 'drop'  },
    'invisible-studs':   { type: 'earrings', style: 'stud'  },
    'invisible-earring': { type: 'earrings', style: 'drop' },
    'statements':        { type: 'earrings', style: 'drop' },
    /* ── Bracelets · Natural ────────────────────────────────────── */
    'natural-diamond-bracelets':    { type: 'bracelet', diamond: 'natural'  },
    /* ── Bracelets · Lab-grown ──────────────────────────────────── */
    'lab-grown-diamond-bracelets':  { type: 'bracelet', diamond: 'labGrown' },
    'tennis-bracelets-lg':          { type: 'bracelet', diamond: 'labGrown', style: 'tennis' },
    'bangles-lg':                   { type: 'bracelet', diamond: 'labGrown', style: 'bangle' },
    /* ── Bracelets · Style (diamond type unknown) ───────────────── */
    'tennis-bracelet':      { type: 'bracelet', style: 'tennis' },
    'bangle':               { type: 'bracelet', style: 'bangle' },
    'invisible-bracelet':   { type: 'bracelet' },
    'champagne-diamond':    { type: 'bracelet' }
  
};

  /* ============================================================
     STATE
     ============================================================ */
  const state = {
    step:  0,
    answers: {
      jewelleryType: null,
      diamond:       null,
      occasion:      null,
      shape:         null,
      style:         null,
      budgetIndex:   1    // default: €2,000–€4,500; adjusted dynamically per type
    },
    budgetUserSet:   false,  // true once the user manually touches the budget slider
    products:        [],
    productMap:      new Map(),    // id → canonical product object (used for Pass B stamping across top-up fetches)
    fieldsCache:     new Map(),
    fetched:         false,
    fetchP:          null,
    fetchStarted:    null,
    fetchSuccessful: false,
    fetchedHandles:  new Set(),    // collection handles already successfully fetched (for lazy/top-up fetch)
    fetchedScope:    null,         // 'all' | 'ring' | 'pendant' | 'earrings' | 'bracelet' — widest scope fetched so far
    advancing:       false,
  };

  /** Jewellery types that have a shape step — all 4 types now supported. */
  const SHAPE_TYPES = new Set(['ring', 'pendant', 'earrings', 'bracelet']);

  /**
   * Returns the active step sequence for the current jewellery type + occasion.
   * See top-of-file comment for the full matrix.
   */
  function getStepSequence() {
    const jt    = state.answers.jewelleryType;
    const occ   = state.answers.occasion;
    const stone = state.answers.diamond;   // 'natural' | 'labGrown' | 'gemstone' | '__skip' | null
    const isRingContext = !jt || jt === '__skip' || jt === 'ring';
    const isRingOnly    = jt === 'ring';
    const hasShape      = !jt || jt === '__skip' || SHAPE_TYPES.has(jt);
    const isGemstone    = stone === 'gemstone';
    const seq = [1, 2];

    // Occasion step — rings only; skip when stone=gemstone (gemstone path skips occasion)
    if (isRingContext && !isGemstone) seq.push(3);

    // Shape step — rings + pendants + earrings + bracelets (all types)
    // Skipped for: ring+birthday/everyday, AND any gemstone search.
    // Gemstone rings don't have cut-specific shape collections (those exist only for
    // diamond rings). Showing shape for gemstone causes near-zero results because
    // 'emerald cut' keyword only matches diamond ring titles, not 'Green Emerald Ring'.
    let showShape = hasShape && !isGemstone;
    if (!isGemstone && isRingOnly && occ && occ !== '__skip') {
      if (occ === 'birthday' || occ === 'everyday') showShape = false;
    }
    if (showShape) seq.push(4);

    // Style step — skipped when stone=gemstone (style is implied by gemstone choice)
    // For rings: only shown for engagement occasion
    let showStyle = !isGemstone;
    if (!isGemstone && isRingOnly && occ && occ !== '__skip') {
      if (occ !== 'engagement') showStyle = false;
    }
    if (showStyle) seq.push(5);

    seq.push(6); // Budget: always
    return seq;
  }

  function getTotal()        { return getStepSequence().length; }
  function getPosition(step) { return getStepSequence().indexOf(step) + 1; }
  function getNextStep(cur)  { const s = getStepSequence(); return s[s.indexOf(cur) + 1] ?? null; }
  function getPrevStep(cur)  { const s = getStepSequence(); return s[s.indexOf(cur) - 1] ?? null; }


  /* ============================================================
     UTILITIES
     ============================================================ */
  const qs      = (s, c = document) => c.querySelector(s);
  const qsa     = (s, c = document) => Array.from(c.querySelectorAll(s));
  const noMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * scrollToStep — scrolls only if the step header is above the visible viewport.
   * When the user is already in the right position (step header is visible),
   * no scroll happens — preventing the jarring jump after clicking a card option.
   *
   * @param {boolean} force — force scroll even if header is already visible (used for
   *   explicit Back/Next button clicks and initial quiz start, NOT for auto-advance).
   */
  function scrollToStep(force = false) {
    if (!wrap) return;
    const stepEl = qs('[data-jf-step="' + state.step + '"]', wrap);
    const header = stepEl ? qs('.jf-step__header', stepEl) : null;
    const target = header || stepEl || wrap;

    const rect = target.getBoundingClientRect();
    const MARGIN = 24; // px above the header — just enough breathing room
    const alreadyVisible = rect.top >= MARGIN && rect.top <= window.innerHeight * 0.4;

    if (!force && alreadyVisible) return; // already in a good position — don't scroll

    const scrollY = target.getBoundingClientRect().top + window.scrollY - MARGIN - 64;
    if (noMotion()) { window.scrollTo(0, Math.max(0, scrollY)); return; }
    window.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
  }

  function announce(msg) {
    const el = qs('[data-jf-live]');
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => { el.textContent = msg; });
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatPrice(cents) {
    if (!cents || cents === 0) return 'Price on request';
    return (cents / 100).toLocaleString('nl-BE', {
      style: 'currency', currency: 'EUR',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    });
  }

  function minPrice(product) {
    if (!product.variants?.length) return 0;
    const avail = product.variants.filter(v => v.available);
    const pool  = avail.length ? avail : product.variants;
    return Math.min(...pool.map(v => parseFloat(v.price) * 100 || 0));
  }

  function isAvailable(product) {
    return product.variants?.some(v => v.available) ?? true;
  }


  /* ============================================================
     ANALYTICS — GTM / GA4 dataLayer
     ============================================================ */
  function track(event, data = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event:                `jewellery_finder_${event}`,
        jf_step:              state.step,
        jf_jewellery_type:    state.answers.jewelleryType  || undefined,
        jf_diamond:           state.answers.diamond        || undefined,
        jf_occasion:          state.answers.occasion       || undefined,
        jf_shape:             state.answers.shape          || undefined,
        jf_style:             state.answers.style          || undefined,
        jf_budget:            CFG.budgetRanges[state.answers.budgetIndex]?.label || undefined,
        ...data
      });
    } catch (_) {}
  }


  /* ============================================================
     DOM REFERENCES
     ============================================================ */
  let wrap, hero, stepsnav, steps, skeleton, resultsEl, errorEl, modal;


  /* ============================================================
     SESSION STORAGE — mid-quiz progress (tab-scoped)
     ============================================================ */
  const SS_KEY = 'zizov_jf_v1_progress';

  function saveProgress() {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ step: state.step, answers: state.answers }));
    } catch (_) {}
  }

  function loadProgress() {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      if (!raw) return false;
      const saved = JSON.parse(raw);
      if (saved?.step > 0 && saved?.answers) {
        state.step    = saved.step;
        state.answers = { ...state.answers, ...saved.answers };
        return true;
      }
    } catch (_) {}
    return false;
  }

  function clearProgress() {
    try { sessionStorage.removeItem(SS_KEY); } catch (_) {}
  }




  /* ============================================================
     URL SHARING
     ============================================================ */
  const URL_MAP = {
    jf_jt: 'jewelleryType',
    jf_d:  'diamond',
    jf_o:  'occasion',
    jf_sh: 'shape',
    jf_s:  'style'
  };

  function generateShareUrl() {
    const p = new URLSearchParams();
    Object.entries(URL_MAP).forEach(([param, key]) => {
      const v = state.answers[key];
      if (v && v !== '__skip') p.set(param, v);
    });
    p.set('jf_b', state.answers.budgetIndex);
    return `${location.origin}${location.pathname}?${p}`;
  }

  function loadFromUrl() {
    const p = new URLSearchParams(location.search);
    if (!p.has('jf_jt') && !p.has('jf_o') && !p.has('jf_b') &&
        !p.has('jf_d')  && !p.has('jf_s') && !p.has('jf_sh')) return false;
    Object.entries(URL_MAP).forEach(([param, key]) => {
      const v = p.get(param);
      if (v && VALID_VALUES[key]?.has(v)) state.answers[key] = v;
    });
    const b = parseInt(p.get('jf_b') ?? '', 10);
    if (!isNaN(b) && b >= 0 && b < CFG.budgetRanges.length) state.answers.budgetIndex = b;
    return true;
  }

  async function shareResults() {
    const url = generateShareUrl();
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Selection — ZIZOV Jewellery Finder', url });
        track('share', { jf_method: 'native_share' });
      } else {
        await navigator.clipboard.writeText(url);
        showShareFeedback();
        track('share', { jf_method: 'clipboard' });
      }
    } catch (_) {}
  }

  function showShareFeedback() {
    const btn = qs('[data-jf-share]', wrap);
    if (!btn) return;
    const orig = btn.innerHTML;
    btn.innerHTML = btn.innerHTML.replace(/Share Results/, 'Link Copied!');
    btn.disabled  = true;
    setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2200);
  }


  /* ============================================================
     FOCUS TRAP — modal accessibility (WCAG 2.1 Level A)
     ============================================================ */
  const FOCUSABLE = 'button:not([disabled]),[href],input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
  let _focusTrapHandler  = null;
  let _sliderSaveTimer   = null;

  function enableFocusTrap(el) {
    const nodes = () => Array.from(el.querySelectorAll(FOCUSABLE));
    _focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = nodes();
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    el.addEventListener('keydown', _focusTrapHandler);
  }

  function disableFocusTrap(el) {
    if (_focusTrapHandler) el.removeEventListener('keydown', _focusTrapHandler);
    _focusTrapHandler = null;
  }


  /* ============================================================
     INITIALISATION
     ============================================================ */
  function init() {
    wrap = qs('#JewelleryFinder');
    if (!wrap) return;

    hero      = qs('.jf-hero',     wrap);
    stepsnav  = qs('.jf-stepsnav', wrap);
    steps     = qsa('.jf-step',    wrap);
    skeleton  = qs('.jf-skeleton', wrap);
    resultsEl = qs('.jf-results',  wrap);
    errorEl   = qs('.jf-error',    wrap);
    modal     = qs('.jf-modal',    wrap);

    if (wrap.dataset.jfAppointmentUrl)   CFG.appointmentUrl    = wrap.dataset.jfAppointmentUrl;
    if (wrap.dataset.jfAllJewelleryUrl)  CFG.allJewelleryUrl   = wrap.dataset.jfAllJewelleryUrl;

    qs('.jf-hero__cta', wrap)?.addEventListener('click', () => {
      startQuiz();
    });


    qsa('.jf-option', wrap).forEach(card => {
      card.addEventListener('click',   onCardClick);
      card.addEventListener('keydown', onCardKey);
    });

    qsa('.jf-nav__btn--back', wrap).forEach(b => b.addEventListener('click', goBack));
    qsa('.jf-nav__btn--next', wrap).forEach(b => b.addEventListener('click', onNext));

    qsa('.jf-stepsnav__item', wrap).forEach((item, i) => {
      item.addEventListener('click', () => {
        const stepNum = parseInt(item.dataset.jfDot, 10);
        if (!stepNum) return;
        const seq = getStepSequence();
        const pos = seq.indexOf(stepNum);
        if (pos !== -1 && pos < seq.indexOf(state.step)) {
          state.step = stepNum;
          showStep(stepNum, true); // stepping back via dot nav → slide in from left
          updateDots();
          scrollToStep(true);
          announce(`Returned to step ${getPosition(stepNum)} of ${getTotal()}.`);
          saveProgress();
          track('step_back', { jf_target_step: stepNum });
        }
      });
      item.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.click(); }
      });
    });

    const slider = qs('.jf-budget__slider', wrap);
    if (slider) slider.addEventListener('input', onSlider);

    qsa('[data-jf-restart]', wrap).forEach(b => b.addEventListener('click', restart));
    qs('[data-jf-share]', wrap)?.addEventListener('click', shareResults);
    qs('[data-jf-save]',  wrap)?.addEventListener('click', saveResults);
    qs('[data-jf-email]', wrap)?.addEventListener('click', openModal);

    // Track main WhatsApp button clicks (not bespoke — those are tracked in showBespoke)
    qs('[data-jf-whatsapp]:not([data-jf-bespoke-wa])', wrap)
      ?.addEventListener('click', () => track('whatsapp_click'));

    // Track empty-state consultation CTA
    qs('.jf-empty__cta', wrap)
      ?.addEventListener('click', () => track('empty_cta_click'));

    // Wire up empty-state action buttons (Change Shape / Change Budget)
    initEmptyStateActions();

    if (modal) {
      qs('.jf-modal__close',   modal)?.addEventListener('click', closeModal);
      qs('.jf-modal__overlay', modal)?.addEventListener('click', closeModal);
      qs('.jf-modal__submit',  modal)?.addEventListener('click', onEmailSubmit);
      modal.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
    }

    qs('.jf-error__retry', wrap)?.addEventListener('click', () => {
      errorEl.classList.remove('is-visible');
      state.fetched         = false;
      state.fetchP          = null;
      state.fetchSuccessful = false;
      state.fetchedHandles  = new Set(); // force a fresh fetch (cache may also be stale)
      state.fetchedScope    = null;
      showResults();
    });

    const fromUrl = loadFromUrl();

    // ?start — skip hero intro, begin quiz at step 1 with no pre-filled answers.
    // Used by the homepage hero CTA: /pages/zizov-jewellery-finder?start
    const autoStart = new URLSearchParams(location.search).has('start');

    // Only auto-start if coming from a shared URL (explicit intent).
    if (fromUrl) {
      startQuiz(true);
    } else if (autoStart) {
      // Auto-start: user explicitly wants the quiz → kick off filtered fetch
      // ('all' until they pick a type, then top-up handled in onCardClick).
      startFetch('all');
      startQuiz();
    } else {
      // Background pre-fetch fallback (v2.6) — only fires if the user lingers
      // on the hero for >4s without starting the quiz. With lazy fetch on type
      // selection + per-collection cache, this is just a UX warm-up for repeat
      // visitors (subsequent fetches are instant from localStorage).
      setTimeout(() => {
        // Only fetch if the user hasn't triggered anything else yet
        if (!state.fetchP) startFetch('all');
      }, 4000);
    }
  }



  /* ============================================================
     QUIZ FLOW
     ============================================================ */
  function startQuiz(skipToResults = false, restore = false) {
    hero.style.display = 'none';
    stepsnav.classList.add('is-visible');

    if (skipToResults) {
      // Restoring from URL — fetch with whatever type the user already picked
      startFetch(state.answers.jewelleryType || 'all');
      showResults();
      return;
    }

    if (!restore) state.step = 1;
    if (state.step < 1) state.step = 1;

    showStep(state.step);
    updateDots();

    const slider = qs('.jf-budget__slider', wrap);
    if (slider) { slider.value = state.answers.budgetIndex; onSlider({ target: slider }); }

    announce(`Step ${getPosition(state.step)} of ${getTotal()}.`);
    // Don't fetch immediately — wait for the user to pick a type so we can
    // filter the collection set. If they answered already (restore flow),
    // the answer is already in state.answers.jewelleryType.
    if (state.answers.jewelleryType) startFetch(state.answers.jewelleryType);
    track('quiz_start');
  }

  function showStep(n, goingBack = false) {
    steps.forEach(s => {
      s.classList.remove('is-active', 'is-going-back');
      s.setAttribute('aria-hidden', 'true');
    });

    const target = qs(`[data-jf-step="${n}"]`, wrap);
    if (!target) return;

    target.classList.add('is-active');
    if (goingBack) target.classList.add('is-going-back');
    target.setAttribute('aria-hidden', 'false');
    // Update aria-label with correct position and total for this user's step sequence.
    // Fixes hardcoded "of 7" being wrong for earrings/bracelets (6 steps only).
    const stepLabel = target.getAttribute('aria-label') || '';
    const stepName  = stepLabel.replace(/Step \d+ of \d+:?\s*/i, '').trim();
    target.setAttribute('aria-label',
      stepName ? `Step ${getPosition(n)} of ${getTotal()}: ${stepName}` : `Step ${getPosition(n)} of ${getTotal()}`
    );

    // Filter options based on current jewellery type (and occasion for rings)
    if (n === 4 || n === 5) filterTypeOptions(target);

    restoreStepSelections(target);

    target.style.animation = 'none';
    target.offsetHeight;
    target.style.animation = '';

    const firstFocus = qs('.jf-option.is-selected, .jf-option, .jf-budget__slider, .jf-nav__btn', target);
    firstFocus?.focus({ preventScroll: true });

    syncNextBtn();
  }

  /**
   * Filter options based on jewellery type (and occasion for ring styles).
   * Options with data-jf-types attr are shown/hidden based on state.answers.jewelleryType.
   * Ring style options are additionally gated on occasion = engagement.
   */
  function filterTypeOptions(stepEl) {
    const jt  = state.answers.jewelleryType;
    const occ = state.answers.occasion;
    const showAll = !jt || jt === '__skip';

    qsa('.jf-option[data-jf-types]', stepEl).forEach(card => {
      if (card.classList.contains('jf-option--skip')) return;
      const types = (card.dataset.jfTypes || '').split(',').map(t => t.trim());
      let visible = showAll || types.includes(jt);

      // Ring styles are only available when occasion = engagement (or not set / skip).
      // Belt-and-suspenders: getStepSequence already hides the entire style step for
      // ring+wedding/birthday/everyday, but we hide individual ring options too in case
      // the step is still rendered (e.g. type = __skip shows all types).
      if (visible && types.length === 1 && types[0] === 'ring') {
        if (jt === 'ring' && occ && occ !== '__skip' && occ !== 'engagement') {
          visible = false;
        }
      }

      card.style.display = visible ? '' : 'none';
      if (!visible) {
        card.classList.remove('is-selected');
        card.setAttribute('aria-checked', 'false');
      }
    });

    // Clear selected style if it no longer applies
    if (!showAll) {
      const sel = qs('.jf-option.is-selected[data-jf-key="style"]', stepEl);
      if (sel) {
        const types = (sel.dataset.jfTypes || '').split(',');
        const isRingStyle = types.length === 1 && types[0] === 'ring';
        const typeOk = types.includes(jt);
        const occasionOk = !isRingStyle || !occ || occ === '__skip' || occ === 'engagement';
        if (!typeOk || !occasionOk) {
          sel.classList.remove('is-selected');
          sel.setAttribute('aria-checked', 'false');
          state.answers.style = null;
          syncNextBtn();
        }
      }
    }
  }

  function restoreStepSelections(stepEl) {
    qsa('.jf-option', stepEl).forEach(card => {
      const key   = card.dataset.jfKey;
      const val   = card.dataset.jfValue;
      if (!key) return;
      const match = state.answers[key] === val;
      card.classList.toggle('is-selected', match);
      card.setAttribute('aria-checked', match ? 'true' : 'false');
    });
  }

  function updateDots() {
    const seq = getStepSequence();
    qsa('.jf-stepsnav__item', wrap).forEach(item => {
      const dotStep = parseInt(item.dataset.jfDot, 10);
      item.classList.remove('is-active', 'is-done', 'is-skipped');

      if (!seq.includes(dotStep)) {
        // Step not in sequence (e.g., shape skipped for earrings)
        item.classList.add('is-skipped');
        item.removeAttribute('tabindex');
        item.removeAttribute('role');
        return;
      }

      const seqPos   = seq.indexOf(dotStep);
      const curPos   = seq.indexOf(state.step);

      if (seqPos < curPos) {
        item.classList.add('is-done');
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'button');
        item.setAttribute('aria-label', `Go back to step ${seqPos + 1}`);
      } else {
        item.removeAttribute('tabindex');
        item.removeAttribute('role');
        item.setAttribute('aria-label', `Step ${seqPos + 1}`);
      }
      if (dotStep === state.step) item.classList.add('is-active');
    });

    const curPos = seq.indexOf(state.step);
    const pct    = seq.length <= 1 ? 0 : (curPos / (seq.length - 1)) * 100;
    stepsnav.style.setProperty('--jf-line-w', pct + '%');

    // Mobile step position indicator (visible only on < 520px where labels are hidden)
    const posEl = qs('[data-jf-step-position]', wrap);
    if (posEl && state.step > 0) {
      posEl.textContent = `Step ${curPos + 1} of ${seq.length}`;
    }
  }

  function onCardClick(e) {
    if (state.advancing) return;

    const card    = e.currentTarget;
    const stepEl  = card.closest('.jf-step');
    const stepNum = parseInt(stepEl.dataset.jfStep, 10);
    const key     = card.dataset.jfKey;
    const val     = card.dataset.jfValue;

    // When jewellery type changes, clear type-specific answers
    if (key === 'jewelleryType' && val !== state.answers.jewelleryType) {
      state.answers.style = null;
      // Occasion is rings-only — clear if switching to a non-ring type
      if (val !== 'ring' && val !== '__skip') {
        state.answers.occasion = null;
      }
      if (val !== '__skip' && !SHAPE_TYPES.has(val)) {
        state.answers.shape = null;
      }

      // Dynamic budget default: adjust slider to the per-category median unless
      // the user has already manually interacted with the budget slider.
      if (!state.budgetUserSet && val !== '__skip') {
        const newDefault = CFG.budgetDefaultByType[val] ?? 2;
        state.answers.budgetIndex = newDefault;
        // Sync slider UI if it exists in the DOM already (avoid setting budgetUserSet)
        const slider = qs('.jf-budget__slider', wrap);
        if (slider) {
          slider.value = newDefault;
          // Call onSlider but temporarily suppress budgetUserSet flag
          const prevUserSet = state.budgetUserSet;
          onSlider({ target: slider });
          state.budgetUserSet = prevUserSet; // restore — this was a programmatic update
        }
      }
    }

    qsa('.jf-option', stepEl).forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
    });

    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');
    state.answers[key] = val;

    // Update dots when type or occasion changes (step sequence might change)
    if (key === 'jewelleryType') {
      updateDots();
      // Lazy fetch (v2.6) — start fetching now that we know the type filter.
      // For '__skip' we fetch all collections; for a specific type we fetch
      // only relevant + cross-type collections, saving ~30% of network calls.
      startFetch(val);
    }

    // When occasion changes for a ring, clear answers that are now in hidden steps
    if (key === 'occasion') {
      const jt = state.answers.jewelleryType;
      if (jt === 'ring' || !jt || jt === '__skip') {
        if (val === 'birthday' || val === 'everyday') {
          state.answers.shape = null;
          state.answers.style = null;
        } else if (val === 'wedding') {
          state.answers.style = null;
        }
        updateDots();
      }
    }

    // When stone type changes to/from gemstone, clear incompatible style/occasion answers
    if (key === 'diamond') {
      if (val === 'gemstone') {
        // Gemstone path: style and occasion are skipped
        state.answers.style   = null;
        state.answers.occasion = null;
        state.answers.shape   = null; // reset shape too — fresh start
      } else {
        // Switching away from gemstone: clear any lingering state
        state.answers.style = null;
      }
      updateDots();
    }

    syncNextBtn();
    saveProgress();
    track('step_answer', { jf_question: key, jf_answer: val });

    // Auto-advance on all steps except budget (step 6)
    if (stepNum !== 6) {
      state.advancing = true;
      const nextBtn = qs('.jf-nav__btn--next', stepEl);
      nextBtn?.classList.add('is-advancing');
      setTimeout(() => {
        state.advancing = false;
        nextBtn?.classList.remove('is-advancing');
        goNext(true); // fromCard=true → no forced scroll
      }, 600);
    }
  }

  function onCardKey(e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); return; }
    const group = qsa('.jf-option:not(.jf-option--skip)', e.currentTarget.closest('.jf-options'));
    const idx   = group.indexOf(e.currentTarget);
    if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && idx < group.length - 1) {
      e.preventDefault(); group[idx + 1].focus();
    }
    if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && idx > 0) {
      e.preventDefault(); group[idx - 1].focus();
    }
  }

  function onNext() {
    const next = getNextStep(state.step);
    next ? goNext() : showResults();
  }

  function goBack() {
    const prev = getPrevStep(state.step);
    if (prev) {
      state.step = prev;
      showStep(state.step, true); // true = going back → slide in from left
      updateDots();
      scrollToStep(true);
      announce(`Went back. Step ${getPosition(state.step)} of ${getTotal()}.`);
      saveProgress();
      track('step_back', { jf_target_step: state.step });
    }
  }

  function goNext(fromCard = false) {
    const next = getNextStep(state.step);
    if (next) {
      state.step = next;
      showStep(state.step);
      updateDots();
      // Auto-advance from card click: only scroll if header is above viewport.
      // Explicit Next button click (fromCard=false): always scroll to orient user.
      scrollToStep(!fromCard);
      announce(`Step ${getPosition(state.step)} of ${getTotal()}.`);
      saveProgress();
      track('step_advance', { jf_to_step: state.step });
    } else {
      showResults();
    }
  }

  function syncNextBtn() {
    const stepEl = qs(`[data-jf-step="${state.step}"]`, wrap);
    if (!stepEl) return;
    const btn = qs('.jf-nav__btn--next', stepEl);
    if (!btn) return;
    const key    = CFG.STEP_KEYS[state.step];
    const locked = key ? state.answers[key] === null : false;
    btn.disabled = locked;
    btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
  }

  function onSlider(e) {
    const val   = parseInt(e.target.value, 10);
    state.answers.budgetIndex = val;
    state.budgetUserSet = true;  // user has manually set budget — don't auto-adjust anymore

    const range = CFG.budgetRanges[val];
    const disp  = qs('.jf-budget__display', wrap);
    if (disp && range) disp.textContent = range.label;

    const pct = (val / (CFG.budgetRanges.length - 1)) * 100;
    e.target.style.background =
      `linear-gradient(to right, var(--rf-accent) ${pct}%, var(--rf-border) ${pct}%)`;

    qsa('.jf-budget__tick', wrap).forEach((tick, i) => {
      tick.classList.toggle('is-active', i <= val);
    });

    e.target.setAttribute('aria-valuenow',  val);
    e.target.setAttribute('aria-valuetext', range?.label ?? '');

    clearTimeout(_sliderSaveTimer);
    _sliderSaveTimer = setTimeout(saveProgress, 300);
  }

  function restart() {
    state.step    = 0;
    state.answers = {
      jewelleryType: null, diamond: null, occasion: null,
      shape: null, style: null, budgetIndex: 2
    };
    state.budgetUserSet = false;
    state.advancing = false;
    clearProgress();
    qsa('.jf-option', wrap).forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
      c.style.display = ''; // restore any filtered options
    });

    const slider = qs('.jf-budget__slider', wrap);
    if (slider) { slider.value = 2; onSlider({ target: slider }); }

    resultsEl.classList.remove('is-visible', 'is-entering');
    qs('.jf-bespoke', wrap)?.classList.remove('is-visible');
    qs('.jf-empty',   wrap)?.classList.remove('is-visible');
    const grid = qs('.jf-products', wrap); if (grid) grid.innerHTML = '';
    errorEl?.classList.remove('is-visible');
    stepsnav.classList.remove('is-visible');
    hero.style.display = '';

    announce('Quiz restarted.');
    scrollToStep(true);
    setTimeout(() => qs('.jf-hero__cta', wrap)?.focus(), 60);
    track('quiz_restart');
  }





  /* ============================================================
     PRODUCT FETCHING
     ============================================================ */
  /* ──────────────────────────────────────────────────────────────
     LOCAL-STORAGE PER-COLLECTION CACHE  (v2.6)

     Each successfully fetched collection result is cached for 1 hour under
     a per-handle key. fetchColl() reads the cache before hitting the network.
     This gives instant loads for returning visitors within the TTL window.
     ────────────────────────────────────────────────────────────── */
  const CACHE_PREFIX = 'zizov_jf_coll_v1__';
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  function cacheRead(handle) {
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj.ts !== 'number' || !Array.isArray(obj.products)) return null;
      if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
      return obj.products;
    } catch (_) {
      return null;
    }
  }

  function cacheWrite(handle, products) {
    try {
      localStorage.setItem(
        CACHE_PREFIX + handle,
        JSON.stringify({ ts: Date.now(), products })
      );
    } catch (e) {
      // Quota exceeded → flush old cache entries and try once more
      try {
        Object.keys(localStorage).forEach(k => {
          if (k.startsWith(CACHE_PREFIX)) localStorage.removeItem(k);
        });
        localStorage.setItem(
          CACHE_PREFIX + handle,
          JSON.stringify({ ts: Date.now(), products })
        );
      } catch (_) { /* give up silently */ }
    }
  }

  async function fetchColl(handle) {
    // Cache-first
    const cached = cacheRead(handle);
    if (cached) return cached;

    const out    = [];
    let   page   = 1;
    const MAX_PG = 4; // cap at 4 pages (1,000 products) — no infinite loop
    while (page <= MAX_PG) {
      let res;
      try {
        res = await fetch(
          `/collections/${handle}/products.json` +
          `?limit=${CFG.perPage}&page=${page}&fields=${CFG.fields}`
        );
      } catch (_) { return null; } // network failure — return null sentinel
      if (!res.ok) break;
      let data;
      try { data = await res.json(); } catch (_) { break; }
      if (!data?.products?.length) break;
      out.push(...data.products);
      if (data.products.length < CFG.perPage) break;
      page++;
    }
    if (out.length > 0) cacheWrite(handle, out);
    return out;
  }

  /* ──────────────────────────────────────────────────────────────
     LAZY FETCH — collection set is filtered by jewellery type  (v2.6)

     getCollectionsForType(type) returns the subset of CFG.collections that
     are relevant for a given type. Collections without a `type` stamp in
     COLLECTION_META_MAP (cross-type, gemstone, style-only) are ALWAYS
     included, because their products may belong to any type.

     Top-up fetches: if the user later widens their scope (e.g. picked
     'ring' → switches to '__skip'), startFetch() will fetch only the
     missing collections, accumulating into state.products.
     ────────────────────────────────────────────────────────────── */
  function getCollectionsForType(typeFilter) {
    if (!typeFilter || typeFilter === '__skip' || typeFilter === 'all') {
      return CFG.collections.slice();
    }
    return CFG.collections.filter(handle => {
      const meta = COLLECTION_META_MAP[handle];
      // Include if no meta (defensive) OR no type stamp OR type matches
      return !meta || !meta.type || meta.type === typeFilter;
    });
  }

  /**
   * Stamp a single product with the meta of the collection it appeared in.
   *  - Pass A (type, diamond): first-seen wins — never overwrite an existing stamp.
   *  - Pass B (shape, style, occasion): always accumulate into Sets.
   * This single helper replaces the two-pass loop and is order-tolerant for
   * Pass A as long as type-specific collections appear before cross-type ones
   * in CFG.collections (which they do, by design).
   */
  function stampProduct(canonical, meta) {
    if (!canonical._jfType    && meta.type)    canonical._jfType    = meta.type;
    if (!canonical._jfDiamond && meta.diamond) canonical._jfDiamond = meta.diamond;
    if (meta.shape) {
      canonical._jfShapes = canonical._jfShapes || new Set();
      canonical._jfShapes.add(meta.shape);
    }
    if (meta.style) {
      canonical._jfStyles = canonical._jfStyles || new Set();
      canonical._jfStyles.add(meta.style);
    }
    if (meta.occasion) {
      canonical._jfOccasion = canonical._jfOccasion || new Set();
      canonical._jfOccasion.add(meta.occasion);
    }
  }

  /**
   * startFetch(typeFilter)
   * Fetches collection products with optional type-scope filtering, caching
   * and top-up support.
   *
   * @param {string|null} typeFilter — 'ring'|'pendant'|'earrings'|'bracelet'|'all'|null
   *   null/'all' fetches every collection in CFG.collections.
   *   A specific type fetches only matching + type-agnostic collections.
   *
   * Idempotent for the same scope. If called with a wider scope after a
   * narrower scope completed, it does a delta fetch of missing collections.
   */
  function startFetch(typeFilter) {
    typeFilter = typeFilter || 'all';

    // Compute desired scope: 'all' supersedes any specific type.
    const desiredScope = typeFilter === '__skip' ? 'all' : typeFilter;
    const wantedHandles = getCollectionsForType(desiredScope);
    const toFetch = wantedHandles.filter(h => !state.fetchedHandles.has(h));

    // Already have everything we need for this scope → return existing promise
    if (toFetch.length === 0) {
      if (!state.fetched) {
        // Edge case: nothing to fetch (shouldn't really happen) — mark done
        state.fetched = true;
        state.fetchSuccessful = true;
        state.fetchP = Promise.resolve();
      }
      return state.fetchP || Promise.resolve();
    }

    // Track widest scope ever requested (so re-renders know what's available)
    if (!state.fetchedScope || desiredScope === 'all') state.fetchedScope = desiredScope;

    if (!state.fetchStarted) state.fetchStarted = Date.now();

    // Chain onto any in-flight fetch to avoid concurrent stamping race conditions.
    const previous = state.fetchP || Promise.resolve();
    const next = previous.then(async () => {
      // Re-check after waiting — another call may have already fetched these.
      const stillToFetch = toFetch.filter(h => !state.fetchedHandles.has(h));
      if (stillToFetch.length === 0) return;

      const results = await Promise.allSettled(stillToFetch.map(fetchColl));
      let anySuccess = false;

      results.forEach((r, idx) => {
        const handle = stillToFetch[idx];
        if (r.status !== 'fulfilled' || !r.value) return;
        anySuccess = true;
        state.fetchedHandles.add(handle);
        const meta = COLLECTION_META_MAP[handle] || {};

        r.value.forEach(p => {
          // Resolve canonical object so stamps land on the SAME object instance
          // even when the API returns duplicate JSON for the same product across
          // multiple collections (different identity, same id).
          let canonical = state.productMap.get(p.id);
          if (!canonical) {
            canonical = p;
            state.productMap.set(p.id, p);
            state.products.push(p);
          }
          stampProduct(canonical, meta);
        });
      });

      // Rebuild fieldsCache for any product whose stamps may have changed.
      // Cheap because state.products only grows (no removal) and buildFields
      // is fast; doing it for all is simpler than tracking dirty flags.
      state.products.forEach(p => state.fieldsCache.set(p.id, buildFields(p)));

      state.fetched = true;
      if (anySuccess) state.fetchSuccessful = true;
    });

    state.fetchP = next;
    return next;
  }


  /* ============================================================
     SCORING ENGINE
     ============================================================ */
  function buildFields(p) {
    /**
     * Build the scoring fields object for a product.
     *
     * Fields used by scoreCriterion (occasion):
     *   title  (weight 3) — primary signal
     *   handle (weight 2) — slug derived from title
     *
     * Special fields stamped by startFetch — Pass A (first-seen collection):
     *   jfType    — jewellery type (ring/pendant/earrings/bracelet or null)
     *   jfDiamond — diamond origin (natural/labGrown or null)
     *
     * Special fields stamped by startFetch — Pass B (accumulated across all):
     *   jfShapes  — Set of shape values from shape-bearing collections
     *               e.g. Set{'oval'} for oval-cut collection membership
     *   jfStyles  — Set of style values from style-bearing collections
     *               e.g. Set{'halo','sidestone'} if in both collections
     *
     * fields.metals — Set of normalised Option2 material strings
     */
    const metals = new Set();
    (p.variants || []).forEach(v => {
      ['option1', 'option2'].forEach(opt => {
        const raw = (v[opt] || '').trim().toLowerCase();
        const val = raw.replace('rosé', 'rose');
        if (val === 'gold' || val === 'rose gold' || val === 'white gold') {
          metals.add(val);
        }
      });
    });

    return {
      title:     (p.title  || '').toLowerCase(),
      handle:    (p.handle || '').toLowerCase(),
      jfType:    p._jfType    || null,
      jfDiamond: p._jfDiamond || null,
      jfShapes:  p._jfShapes  || null,   // Set<string> from collection stamps, or null
      jfStyles:  p._jfStyles  || null,   // Set<string> from collection stamps, or null
      jfOccasion:p._jfOccasion || null,   // Set<string> from occasion-bearing collections
      metals
    };
  }

  function getFields(p) {
    if (state.fieldsCache.has(p.id)) return state.fieldsCache.get(p.id);
    const f = buildFields(p);
    state.fieldsCache.set(p.id, f);
    return f;
  }

  /**
   * Score one criterion with cumulative field bonus.
   * Returns null if skipped, number otherwise.
   */
  function scoreCriterion(fields, key, val) {
    if (!val || val === '__skip') return null;
    const kws = CFG.keywords[key]?.[val];
    if (!kws?.length) return 0;

    let best   = 0;
    let extras = 0;

    Object.entries(CFG.FIELD_WEIGHTS).forEach(([field, weight]) => {
      if (kws.some(kw => fields[field].includes(kw))) {
        if (weight > best) { extras += (best > 0 ? 1 : 0); best = weight; }
        else               { extras++; }
      }
    });

    return best + Math.min(extras * 0.5, 1.5);
  }

  /**
   * Jewellery type scoring — collection-based primary, keyword fallback.
   *
   * Priority 1 (fields.jfType set via COLLECTION_META_MAP):
   *   match   → +4.5  (max possible for this criterion)
   *   mismatch → -4.0  (strong enough to overcome full budget score of +3.0)
   *              The product is also flagged as a confirmed mismatch for the
   *              short-circuit in scoreProduct.
   *
   * Priority 2 (fields.jfType is null — product in no type-exclusive collection):
   *   Fall back to keyword scoring on title + handle ONLY.
   *   Tags excluded (earring tags incorrectly contain "Rings" for 44/45 products).
   *   ' ring' (space-prefixed) prevents false match inside 'earrings'.
   *   '-ring-' checks handles for ring products without 'ring' as a standalone word.
   *   Returns 0 when no keyword evidence found (ambiguous, not penalised).
   *
   * Returns: number (scoring contribution) or null (skipped).
   * Also sets _jfMismatch = true on the product object when a confirmed mismatch
   * is detected, enabling scoreProduct's short-circuit exit.
   */
  function scoreJewelleryType(p, fields, val) {
    if (!val || val === '__skip') return null;

    // ── Priority 1: collection-based (authoritative) ──────────────
    if (fields.jfType) {
      if (fields.jfType === val) {
        p._jfMismatch = false;
        return 4.5;    // perfect match
      } else {
        p._jfMismatch = true;
        return -4.0;   // confirmed wrong type
      }
    }

    // ── Priority 2: keyword fallback (title + handle only) ────────
    // Use only the safe fallback keywords defined in CFG.keywords.jewelleryType.
    // These use space-prefixed patterns (' ring') that can't false-match 'earrings'.
    const kws = CFG.keywords.jewelleryType[val] || [];
    const hit = kws.some(kw =>
      fields.title.includes(kw) || fields.handle.includes(kw)
    );
    if (hit) return 3.0;  // positive keyword signal — good but not perfect

    // Check for a different type to apply a soft penalty
    const others = CFG.JEWELLERY_TYPE_KEYS.filter(k => k !== val);
    for (const other of others) {
      const oKws = CFG.keywords.jewelleryType[other] || [];
      if (oKws.some(kw => fields.title.includes(kw) || fields.handle.includes(kw))) {
        return -2.0;  // soft mismatch (keyword-based, less certain than collection)
      }
    }

    return 0;  // no evidence either way — don't penalise
  }

  /**
   * Stone type scoring — handles natural diamonds, lab-grown diamonds, and gemstones.
   *
   * val = 'natural':
   *   Collection match → +3.0. LG signal in title → hard-block. No signals → +1.5.
   *
   * val = 'labGrown':
   *   Collection match → +3.0. No LG signal → hard-block (all LG have "(LG)" in title).
   *
   * val = 'gemstone':
   *   Product has gemstone style stamp  → +4.5 (authoritative — it IS a gemstone product).
   *   Product has diamond-only stamp and NO gemstone style → hard-block.
   *   Product has LG signal but also gemstone style → +3.0 (LG gemstone is valid).
   *   No stamps → keyword fallback (sapphire/ruby/etc in title → +2.0).
   *   No gemstone signals → hard-block (don't mix diamond rings into gemstone results).
   */
  function scoreDiamond(fields, val, p) {
    if (!val || val === '__skip') return null;

    // ── GEMSTONE ──────────────────────────────────────────────────
    if (val === 'gemstone') {
      // Product confirmed as gemstone via collection style stamp
      if (fields.jfStyles?.has('gemstone')) {
        // LG gemstone (red ruby LG earrings etc.) is fine for gemstone search
        return 4.5;
      }
      // Product is confirmed diamond-only (has jfDiamond but no gemstone style)
      if (fields.jfDiamond) {
        p._jfDiamondMismatch = true;
        return -99;
      }
      // Keyword fallback: check for gemstone keywords in title
      const gemKws = CFG.keywords.diamond.gemstone;
      const hasGemSignal = gemKws.some(kw => fields.title.includes(kw));
      if (hasGemSignal) return 2.0;
      // No gemstone signals at all — hard block to keep results clean
      p._jfDiamondMismatch = true;
      return -99;
    }

    // ── NATURAL / LABGROWN ────────────────────────────────────────
    // First: if product is a gemstone product, block it from diamond searches
    if (fields.jfStyles?.has('gemstone') && !fields.jfDiamond) {
      // Pure gemstone product (no diamond stamp) — not relevant for diamond search
      p._jfDiamondMismatch = true;
      return -99;
    }

    // Priority 1: collection-based diamond stamp (authoritative)
    // Exception: if the collection says 'natural' but the title contains an LG signal
    // (e.g. "(LG)"), the title is more reliable — treat as LG. This handles cases where
    // an LG product is accidentally added to a natural collection in Shopify.
    if (fields.jfDiamond) {
      const lgKwsCheck = CFG.keywords.diamond.labGrown;
      const titleIsLG  = lgKwsCheck.some(kw => fields.title.includes(kw) || fields.handle.includes(kw));
      if (fields.jfDiamond === 'natural' && titleIsLG) {
        // Trust title: treat as LG despite collection stamp
        if (val === 'labGrown') return 2.5;
        p._jfDiamondMismatch = true;
        return -99;
      }
      if (fields.jfDiamond === val) return 3.0;
      p._jfDiamondMismatch = true;
      return -99;
    }

    // Priority 2: title inference
    const lgKws = CFG.keywords.diamond.labGrown;
    const hasLabSignal = lgKws.some(kw =>
      fields.title.includes(kw) || fields.handle.includes(kw)
    );

    if (val === 'labGrown') {
      if (hasLabSignal) return 2.5;
      p._jfDiamondMismatch = true;
      return -99;
    }
    // val === 'natural'
    if (hasLabSignal) {
      p._jfDiamondMismatch = true;
      return -99;
    }
    return 1.5;
  }

  /**
   * Shape scoring — STRICT MATCH (v2.6).
   *
   * When the user picks a shape, ONLY products with positive shape evidence
   * survive. Products with confirmed-different shape OR no shape evidence at
   * all are hard-blocked (-99), even if their other criteria score perfectly.
   *
   *   1. Collection-stamped match    → +5.0   (authoritative match)
   *   2. Collection-stamped mismatch → -99    (HARD: confirmed wrong shape)
   *   3. No collection stamp + title keyword hit  → +3.0
   *   4. No collection stamp + no keyword         → -99 (HARD: no evidence)
   *   5. No keywords defined for this shape       → -99 (HARD: cannot score)
   *
   * Sets p._jfShapeMismatch when a hard block is needed so scoreProduct() can
   * short-circuit cleanly (mirrors the pattern used by scoreJewelleryType and
   * scoreDiamond).
   */
  function scoreShape(fields, val, p) {
    if (!val || val === '__skip') return null;

    // ── Priority 1: collection-based stamp (authoritative) ──────────
    if (fields.jfShapes) {
      if (fields.jfShapes.has(val)) return 5.0;     // ✓ confirmed match
      p._jfShapeMismatch = true;
      return -99;                                    // ✗ confirmed different shape — HARD BLOCK
    }

    // ── Priority 2: title keyword fallback ──────────────────────────
    const kws = CFG.keywords.shape?.[val];
    if (!kws?.length) {
      p._jfShapeMismatch = true;
      return -99;                                    // ✗ no keywords defined — HARD BLOCK
    }
    const t = fields.title;
    if (kws.some(kw => t.includes(kw))) return 3.0;  // ✓ keyword evidence

    // No collection-stamp, no keyword hit → cannot prove this is the right shape
    p._jfShapeMismatch = true;
    return -99;                                      // ✗ no shape evidence — HARD BLOCK
  }

  /**
   * Style scoring — collection-first (v1.6), keyword fallback.
   *
   * Priority 1 (fields.jfStyles set via COLLECTION_META_MAP pass B):
   *   match   → +4.5  (product is in the exact style collection the user wants)
   *   mismatch → -1.0  (softer penalty — many products have multiple valid styles,
   *              and style collections are not exhaustive; a ring not in the halo
   *              collection may still BE a halo ring from a cross-listing collection)
   *
   * Priority 2 (fields.jfStyles is null):
   *   Falls back to scoreCriterion keyword matching on title + handle (unchanged).
   */
  function scoreStyle(fields, val) {
    if (!val || val === '__skip') return null;

    if (fields.jfStyles) {
      if (fields.jfStyles.has(val)) return 5.0;  // confirmed match via collection (+raised from 4.5)

      // For gemstone style: if the product is confirmed in a non-gemstone style
      // collection (solitaire, halo, etc.) but user wants gemstone — hard miss.
      // For all other style mismatches: soft penalty; style collections aren't exhaustive
      // and a ring in 'solitaire-rings' may still be appropriate for other styles.
      return val === 'gemstone' ? -3.0 : -2.0;  // raised from -1.5
    }

    // Fallback: keyword scoring on title + handle
    return scoreCriterion(fields, 'style', val);
  }



  /**
   * Occasion scoring — collection-first, keyword fallback.
   *
   * Priority 1 (fields.jfOccasion set via COLLECTION_META_MAP occasion stamps):
   *   match    → +3.0  (product is in the occasion-appropriate collection)
   *   mismatch → -2.0  (soft — most products aren't in occasion collections so we
   *              don't hard-block; the step logic already filters what the user sees)
   *
   * Priority 2 (fields.jfOccasion is null):
   *   Falls back to keyword scoring (titles/handles — wedding eternity rings, etc.)
   *
   * Added to maxPossible only when a collection match is possible (jfOccasion not null)
   * or keyword evidence exists — handled dynamically in scoreProduct.
   */
  function scoreOccasion(fields, val) {
    if (!val || val === '__skip') return null;

    // Everyday = any ring (general diamond rings collection). Don't score occasion
    // because stamping natural-diamond-rings with 'everyday' would also stamp
    // engagement rings, causing conflicts. Instead, treat everyday as a soft bonus
    // based on keywords only (daily diamond, minimalist, etc.) — no hard penalties.
    if (val === 'everyday') {
      // Small bonus for explicitly everyday products, no penalty for others
      return scoreCriterion(fields, 'occasion', val);
    }

    if (fields.jfOccasion) {
      if (fields.jfOccasion.has(val)) return 3.0;
      // Engagement and Wedding are "soft siblings" — both are formal diamond ring occasions.
      // A radiant solitaire (engagement stamp) is perfectly appropriate for a wedding;
      // an eternity band (wedding stamp) is sometimes given as an engagement gift too.
      // Only apply soft penalty (-0.5) between these two; use hard penalty (-1.5) for
      // clearly different occasions (birthday/everyday vs. engagement/wedding).
      const formalOcc = new Set(['engagement', 'wedding']);
      if (formalOcc.has(val) && [...fields.jfOccasion].some(o => formalOcc.has(o))) {
        return -0.5;  // soft sibling mismatch — shape/style still dominate
      }
      return -1.5;  // clearer mismatch (birthday vs. engagement, etc.)
    }

    // Fallback: keyword scoring
    return scoreCriterion(fields, 'occasion', val);
  }

  /**
   * Budget scoring: exact range = 3 pts, ±20% soft zone = 1.5 pts.
   * Brackets are now genuinely wider (Under €2k → €2-4.5k → €4.5-8k → €8-16k → €16-32k → €32k+)
   * so the soft zone can be reduced back to ±20% — brackets themselves are broader.
   */
  function scoreBudget(product) {
    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (!range) return 0;
    const pr = minPrice(product);
    if (pr === 0) return 1;
    if (pr >= range.min && pr <= range.max) return 3;
    const softHigh = range.max === Infinity ? Infinity : range.max * 1.20;
    const softLow  = range.min * 0.80;
    if (pr <= softHigh && pr >= softLow) return 1.5;
    // Hard cut: completely exclude products more than 2× the budget ceiling
    // or less than 40% of the budget floor. An €18k ring should never appear
    // in a €2-4.5k search regardless of how well it matches other criteria.
    const hardHigh = range.max === Infinity ? Infinity : range.max * 2.0;
    const hardLow  = range.min * 0.40;
    if (pr > hardHigh || pr < hardLow) return -99; // scoreProduct checks for this
    return -1.5;  // Outside soft zone but within hard zone → small penalty
  }

  function scoreProduct(p) {
    // Exclude concept products (€0 price — internal placeholders, not customer-facing).
    if (minPrice(p) === 0) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }

    // Reset stale flags from any previous scoring run.
    p._jfMismatch        = false;
    p._jfDiamondMismatch = false;
    p._jfShapeMismatch   = false;

    const fields          = getFields(p);
    const matchedCriteria = [];
    let totalScore  = 0;
    let maxPossible = 0;

    // ── 1. Jewellery Type (highest priority) ─────────────────────────
    const jtScore = scoreJewelleryType(p, fields, state.answers.jewelleryType);
    if (jtScore !== null) {
      maxPossible += 4.5;
      totalScore  += jtScore;
      if (jtScore > 0) matchedCriteria.push('jewelleryType');
    }
    // Short-circuit: confirmed collection-based type mismatch.
    if (p._jfMismatch === true) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }

    // ── 2. Diamond type — strict hard-block ──────────────────────────
    // scoreDiamond now sets p._jfDiamondMismatch = true and returns -99
    // when the product's diamond type is confirmed wrong (collection or LG signal).
    const diamondScore = scoreDiamond(fields, state.answers.diamond, p);
    if (diamondScore !== null) {
      maxPossible += 3.0;
      totalScore  += Math.max(diamondScore, -3);  // cap contribution for matchPct calc
      if (diamondScore > 0) matchedCriteria.push('diamond');
    }
    if (p._jfDiamondMismatch === true) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }

    // ── 2.5 Auto-style when stone=gemstone ─────────────────────────
    // When user picks Gemstone as stone type, style is implicitly 'gemstone'.
    // We don't score style separately — the stone score already captures this.
    const isGemstoneSearch = state.answers.diamond === 'gemstone';

    // ── 3. Occasion — collection-first, keyword fallback ──────────────
    // Now uses scoreOccasion which checks jfOccasion Set (collection-based) first.
    // natural-engagement-rings is stamped with occasion:'engagement', so engagement
    // rings get +3.0 and other rings get -2.0 (soft mismatch) or keyword fallback.
    const occasionScore = scoreOccasion(fields, state.answers.occasion);
    if (occasionScore !== null) {
      // Add to maxPossible when collection-based occasion signal is available,
      // otherwise treat as a soft bonus (don't inflate maxPossible for keyword hits).
      if (fields.jfOccasion) maxPossible += 3.0;
      totalScore += occasionScore;
      if (occasionScore > 0) matchedCriteria.push('occasion');
    }

    // ── 4. Style — skipped when stone=gemstone (implied by stone choice) ──
    const styleScore = isGemstoneSearch ? null : scoreStyle(fields, state.answers.style);
    if (styleScore !== null) {
      maxPossible += 4.5;
      totalScore  += styleScore;
      if (styleScore > 0) matchedCriteria.push('style');
    }

    // ── 5. Shape — STRICT MATCH (v2.6). Skipped for gemstone path. ────
    // scoreShape sets p._jfShapeMismatch = true and returns -99 when the
    // product cannot be confirmed as the user-selected shape (either by
    // collection stamp or by title keyword evidence).
    const shapeScore = isGemstoneSearch ? null : scoreShape(fields, state.answers.shape, p);
    if (shapeScore !== null) {
      maxPossible += 4.5;
      totalScore  += shapeScore;
      if (shapeScore > 0) matchedCriteria.push('shape');
    }
    // Hard-block: confirmed shape mismatch OR no shape evidence at all.
    // Placed BEFORE budget so we skip budget scoring for already-rejected products.
    if (p._jfShapeMismatch === true) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }

    // ── 6. Budget ─────────────────────────────────────────────────────
    maxPossible += 3;
    const budgetScore = scoreBudget(p);
    if (budgetScore === -99) {
      return { product: p, score: -99, matchPct: 0, matchedCriteria: [] };
    }
    totalScore  += budgetScore;
    if (budgetScore >= 1.5) matchedCriteria.push('budget');

    // matchPct = proportion of asked criteria that this product actually matched.
    // Count-based (not score-ratio) so penalties don't hide the badge on good products.
    const totalCriteriaAsked = [
      state.answers.jewelleryType, state.answers.diamond, state.answers.occasion,
      state.answers.style, state.answers.shape
    ].filter(v => v !== null && v !== '__skip').length + 1; // +1 for budget
    const matchPct = totalCriteriaAsked > 0
      ? Math.round((matchedCriteria.length / totalCriteriaAsked) * 100)
      : 0;

    return { product: p, score: totalScore, matchPct, matchedCriteria };
  }

  /**
   * Rank products by score and return top maxResults.
   *
   * When all quiz questions are skipped (hasAnswers = false), we skip score
   * filtering entirely and return the top 24 by availability then price — so
   * the results page is never empty just because the user skipped everything.
   *
   * When questions are answered, confirmed type-mismatches score -99 and are
   * excluded by the score > 0 filter.
   */
  function rankProducts() {
    const scored = state.products.map(scoreProduct);

    // Detect whether the user answered at least one question
    const hasAnswers = Object.entries(state.answers).some(([k, v]) => {
      if (k === 'budgetIndex') return false;  // budget always has a default value
      return v !== null && v !== '__skip';
    });

    const pool = hasAnswers
      ? scored.filter(r => r.score > 0)
      : scored.filter(r => r.score > -99); // exclude confirmed mismatches even in fallback

    return pool
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Secondary sort: available first, then by ascending price
        const aA = isAvailable(a.product) ? 0 : 1;
        const bA = isAvailable(b.product) ? 0 : 1;
        if (aA !== bA) return aA - bA;
        return minPrice(a.product) - minPrice(b.product);
      })
      .slice(0, CFG.maxResults);
  }


  /* ============================================================
     SHOW RESULTS
     ============================================================ */
  async function showResults() {
    steps.forEach(s => { s.classList.remove('is-active'); s.setAttribute('aria-hidden', 'true'); });
    stepsnav.classList.remove('is-visible');
    errorEl?.classList.remove('is-visible');

    if (state.answers.budgetIndex === CFG.BESPOKE_INDEX) { showBespoke(); return; }

    skeleton?.classList.add('is-visible');
    scrollToStep(true);
    announce('Searching our collection for your perfect match…');

    // Defensive: if no fetch has been kicked off yet (very fast users, edge
    // cases), trigger one now using the widest scope we know about.
    if (!state.fetched) startFetch(state.answers.jewelleryType || 'all');
    const fetchStart = state.fetchStarted || Date.now();

    try { await state.fetchP; } catch (_) {
      skeleton?.classList.remove('is-visible');
      errorEl?.classList.add('is-visible');
      scrollToStep(true); track('results_error'); return;
    }

    if (!state.fetchSuccessful) {
      skeleton?.classList.remove('is-visible');
      errorEl?.classList.add('is-visible');
      scrollToStep(true); track('results_error'); return;
    }

    const elapsed = Date.now() - fetchStart;
    if (elapsed < 700) await new Promise(r => setTimeout(r, 700 - elapsed));

    skeleton?.classList.remove('is-visible');

    const ranked = rankProducts();
    renderResults(ranked);

    resultsEl.classList.add('is-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      resultsEl.classList.remove('is-entering');
      resultsEl.classList.add('is-visible');
    }));

    clearProgress();
    announce(
      ranked.length === 0
        ? 'No items found. Book a free consultation for personal advice.'
        : `${ranked.length} item${ranked.length !== 1 ? 's' : ''} matched your preferences.`
    );
    track('results_shown', {
      jf_result_count:         ranked.length,
      jf_result_count_zero:    ranked.length === 0 ? 1 : 0,
      jf_strict_shape_active:  !!(state.answers.shape && state.answers.shape !== '__skip') ? 1 : 0,
      jf_jewellery_type:       state.answers.jewelleryType || 'none',
      jf_diamond:              state.answers.diamond       || 'none',
      jf_shape:                state.answers.shape         || 'none',
      jf_style:                state.answers.style         || 'none'
    });
  }

  function showBespoke() {
    skeleton?.classList.remove('is-visible');
    const bespokeEl = qs('.jf-bespoke', wrap);
    if (!bespokeEl) return;

    // Clear count text and hide summary tags — not relevant for bespoke tier
    const countEl = qs('.jf-results__count', wrap);
    if (countEl) countEl.textContent = '';
    const summaryEl = qs('.jf-results__summary', wrap);
    if (summaryEl) summaryEl.innerHTML = '';

    const cta = qs('.jf-bespoke__cta', bespokeEl);
    if (cta) {
      cta.href = CFG.appointmentUrl;
      cta.setAttribute('aria-label', 'Request Private Consultation (opens in new window)');
      cta.addEventListener('click', () => track('bespoke_consultation_click'), { once: true });
    }
    const waBtn = qs('[data-jf-bespoke-wa]', bespokeEl);
    if (waBtn) {
      waBtn.addEventListener('click', () => track('bespoke_whatsapp_click'), { once: true });
    }
    updateWhatsappLink();
    bespokeEl.classList.add('is-visible');
    resultsEl.classList.add('is-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      resultsEl.classList.remove('is-entering');
      resultsEl.classList.add('is-visible');
    }));
    scrollToStep(true);
    announce('Your preferences suggest a bespoke creation. Our Antwerp experts await.');
    track('results_bespoke');
  }


  /* ============================================================
     RENDER RESULTS
     ============================================================ */
  const FALLBACK = 'https://cdn.shopify.com/s/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif';

  function imgSrc(raw, w) {
    if (!raw || raw.includes('no-image')) return raw || FALLBACK;
    return raw.replace(/(\.(jpg|jpeg|png|webp|gif))(\?|$)/i, `_${w}x$1$3`);
  }

  function renderResults(ranked) {
    renderTags();

    const countEl   = qs('.jf-results__count',  wrap);
    const grid      = qs('.jf-products',         wrap);
    const emptyEl   = qs('.jf-empty',            wrap);
    const bespokeEl = qs('.jf-bespoke',          wrap);

    if (countEl) {
      countEl.textContent = ranked.length > 0
        ? `${ranked.length} item${ranked.length !== 1 ? 's' : ''} matched your preferences`
        : '';
    }

    bespokeEl?.classList.remove('is-visible');
    if (!grid) return;

    if (ranked.length === 0) {
      grid.innerHTML = '';
      if (emptyEl) {
        emptyEl.classList.add('is-visible');
        renderEmptyStateContent(emptyEl);
        const cta = qs('.jf-empty__cta', emptyEl);
        if (cta) {
          cta.href = CFG.appointmentUrl;
          cta.setAttribute('aria-label', 'Book a Free Consultation (opens in new window)');
        }
      }
      return;
    }

    if (emptyEl) emptyEl.classList.remove('is-visible');

    grid.innerHTML = ranked.map(({ product: p, matchPct, matchedCriteria }, i) => {
      const available  = isAvailable(p);
      const raw1       = p.images?.[0]?.src ?? FALLBACK;
      const raw2       = p.images?.[1]?.src ?? null;
      const price          = minPrice(p);
      const matchedVariant = p.variants?.[0];
      const compare        = parseFloat(matchedVariant?.compare_at_price ?? 0) * 100;
      const hasDisc        = compare > price && compare > 0;
      const productUrl     = `/products/${esc(p.handle)}`;

      const priceHtml = hasDisc
        ? `<span class="is-sale">${formatPrice(price)}</span><s>${formatPrice(compare)}</s>`
        : formatPrice(price);

      let badge = '', badgeClass = '';
      if      (matchPct >= 90) { badge = '★ Perfect Match'; badgeClass = 'jf-product__badge--perfect'; }
      else if (matchPct >= 75) { badge = '✦ Great Match';   badgeClass = 'jf-product__badge--great';   }
      else if (matchPct >= 60) { badge = 'Good Match';      badgeClass = 'jf-product__badge--good';    }

      const breakdown = matchedCriteria.map(k => {
        if (k === 'budget') return CFG.budgetRanges[state.answers.budgetIndex]?.label ?? 'Budget';
        const val = state.answers[k];
        return LABELS[k]?.[val] ?? val;
      }).filter(Boolean).join(' · ');

      const imgHtml = raw2
        ? `<img src="${esc(imgSrc(raw1,600))}" srcset="${esc(imgSrc(raw1,600))} 1x, ${esc(imgSrc(raw1,1200))} 2x" alt="${esc(p.title)}" class="jf-product__img jf-product__img--primary" loading="lazy" width="400" height="400">
           <img src="${esc(imgSrc(raw2,600))}" srcset="${esc(imgSrc(raw2,600))} 1x, ${esc(imgSrc(raw2,1200))} 2x" alt="" class="jf-product__img jf-product__img--alt" loading="lazy" width="400" height="400">`
        : `<img src="${esc(imgSrc(raw1,600))}" srcset="${esc(imgSrc(raw1,600))} 1x, ${esc(imgSrc(raw1,1200))} 2x" alt="${esc(p.title)}" class="jf-product__img" loading="lazy" width="400" height="400">`;

      const delay = Math.min(i * 0.055, 0.5).toFixed(2);

      return `
        <a href="${productUrl}"
           role="listitem" class="jf-product${!available ? ' is-sold-out' : ''}"
           aria-label="${esc(p.title)}${!available ? ' (sold out)' : ''}"
           style="animation-delay:${delay}s"
           data-jf-available="${available ? '1' : '0'}"
           data-handle="${esc(p.handle)}">
          <div class="jf-product__img-wrap">
            ${imgHtml}
            ${badge ? `
              <span class="jf-product__badge ${badgeClass}" aria-label="${badge}. Matched: ${esc(breakdown)}">
                ${badge}
                ${breakdown ? `<span class="jf-product__badge-tip">${esc(breakdown)}</span>` : ''}
              </span>` : ''}
            ${!available ? `<div class="jf-product__sold-out"><span>Sold Out</span></div>` : ''}
          </div>
          <div class="jf-product__info">
            <h3 class="jf-product__title">${esc(p.title)}</h3>
            <p class="jf-product__price">${priceHtml}</p>
          </div>
        </a>`;
    }).join('');

    applyProductVisibility();
    initBadgeTooltips();

    // ── Product click tracking ──────────────────────────────────────────
    const productGrid = qs('.jf-results__grid', wrap);
    if (productGrid) {
      productGrid.addEventListener('click', e => {
        const card = e.target.closest('.jf-product');
        if (!card) return;
        track('product_click', {
          jf_product_handle: card.dataset.handle || '',
          jf_product_title:  card.getAttribute('aria-label') || '',
        });
      });
    }

    // ── WhatsApp CTA: update href with pre-filled quiz summary ──────────
    updateWhatsappLink();
  }

  /**
   * renderEmptyStateContent — populates the empty-state message and action
   * buttons based on which strict filters the user applied.  (v2.6)
   *
   * Logic:
   *  - If the user picked a specific shape, surface a "Change Shape" button
   *    (jumps back to step 4) and a "Change Budget" button (jumps to step 6).
   *  - If only budget is restrictive, surface "Change Budget" only.
   *  - In all cases the existing "Book a Free Consultation" CTA stays as the
   *    primary fallback.
   *
   * The buttons are wired up in initEmptyStateActions() (called once at init).
   */
  function renderEmptyStateContent(emptyEl) {
    const titleEl   = qs('.jf-empty__title',   emptyEl);
    const textEl    = qs('.jf-empty__text',    emptyEl);
    const actionsEl = qs('.jf-empty__actions', emptyEl);
    if (!titleEl || !textEl) return;

    const hasShape = state.answers.shape && state.answers.shape !== '__skip';

    let title, text;
    const actions = [];

    if (hasShape) {
      title = 'No Matches in This Shape';
      text  = 'No products were found in this shape within your budget. Try a different shape, widen your budget, or book a free consultation.';
      actions.push({ label: 'Change Shape',  step: 4, key: 'shape'  });
      actions.push({ label: 'Change Budget', step: 6, key: 'budget' });
    } else {
      title = 'No Exact Matches Found';
      text  = 'No products match your selection within this budget. Try widening your budget, or book a free consultation for a curated selection.';
      actions.push({ label: 'Change Budget', step: 6, key: 'budget' });
    }

    titleEl.textContent = title;
    textEl.textContent  = text;

    if (actionsEl) {
      // Filter to actions whose target step is actually in the current sequence
      const seq = getStepSequence();
      const validActions = actions.filter(a => seq.includes(a.step));
      actionsEl.innerHTML = validActions.map(a => `
        <button type="button" class="jf-empty__action" data-jf-empty-step="${a.step}" data-jf-empty-key="${esc(a.key)}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="14" height="14">
            <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8"/><path d="M21 3v5h-5"/>
          </svg>
          ${esc(a.label)}
        </button>
      `).join('');
    }
  }

  /**
   * goToStep — direct navigation to a specific step from the empty state.
   * Hides results UI, restores the steps-nav, and scrolls to the target step.
   *  (v2.6)
   */
  function goToStep(targetStep) {
    if (!getStepSequence().includes(targetStep)) return;

    // Hide results / empty / bespoke surfaces
    resultsEl?.classList.remove('is-visible', 'is-entering');
    qs('.jf-empty',   wrap)?.classList.remove('is-visible');
    qs('.jf-bespoke', wrap)?.classList.remove('is-visible');
    skeleton?.classList.remove('is-visible');

    // Show steps nav and target step
    stepsnav.classList.add('is-visible');
    state.step = targetStep;
    showStep(targetStep);
    updateDots();
    scrollToStep(true);

    announce(`Step ${getPosition(targetStep)} of ${getTotal()}.`);
    saveProgress();
    track('empty_state_jump', { jf_target_step: targetStep });
  }

  /**
   * Wire up clicks on dynamically-rendered "Change Shape" / "Change Budget"
   * buttons inside the empty-state container. Delegated listener — safe even
   * after the buttons are re-rendered.  (v2.6)
   */
  function initEmptyStateActions() {
    const emptyEl = qs('.jf-empty', wrap);
    if (!emptyEl) return;
    emptyEl.addEventListener('click', e => {
      const btn = e.target.closest('[data-jf-empty-step]');
      if (!btn) return;
      const target = parseInt(btn.dataset.jfEmptyStep, 10);
      if (!Number.isFinite(target)) return;
      track('empty_state_action', { jf_action: btn.dataset.jfEmptyKey || 'unknown' });
      goToStep(target);
    });
  }

  /**
   * Badge tooltips — desktop: CSS :hover / :focus-within.
   * Mobile (touch): tap the badge to toggle. Tap anywhere else to dismiss.
   * The tooltip is already in the DOM via renderResults — just needs event wiring.
   */
  function initBadgeTooltips() {
    const grid = qs('.jf-products', wrap);
    if (!grid) return;

    // Single delegated listener on the grid — badges may be re-rendered
    grid.addEventListener('click', function onBadgeClick(e) {
      const badge = e.target.closest('.jf-product__badge');
      if (!badge) {
        // Tap outside → close all
        qsa('.jf-product__badge.is-tip-open', grid).forEach(b => b.classList.remove('is-tip-open'));
        return;
      }
      // This is only needed on touch devices; skip on pointer:fine (mouse)
      if (!window.matchMedia('(pointer: coarse)').matches) return;
      e.preventDefault();
      const wasOpen = badge.classList.contains('is-tip-open');
      qsa('.jf-product__badge.is-tip-open', grid).forEach(b => b.classList.remove('is-tip-open'));
      if (!wasOpen) badge.classList.add('is-tip-open');
    }, { passive: false });
  }

  function applyProductVisibility() {
    // All products on Zizov are in stock — no sold-out filtering needed.
    // Function kept for structural compatibility (called after renderResults).
  }




  /* ============================================================
     RENDER TAGS — uses LABELS, no duplication
     ============================================================ */
  function renderTags() {
    const container = qs('.jf-results__summary', wrap);
    if (!container) return;

    const tags = [];

    ['jewelleryType', 'diamond', 'occasion', 'style', 'shape'].forEach(key => {
      const val = state.answers[key];
      if (!val || val === '__skip' || !LABELS[key]?.[val]) return;
      tags.push(`<span class="jf-results__tag">${esc(LABELS[key][val])}</span>`);
    });

    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (range) tags.push(`<span class="jf-results__tag">${esc(range.label)}</span>`);

    container.innerHTML = tags.join('');
  }

  function buildPreferencesSummary() {
    const lines = [];
    ['jewelleryType', 'diamond', 'occasion', 'style', 'shape'].forEach(key => {
      const val = state.answers[key];
      if (val && val !== '__skip' && LABELS[key]?.[val]) lines.push(`${key}: ${LABELS[key][val]}`);
    });
    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (range) lines.push(`budget: ${range.label}`);
    return lines.join(', ');
  }


  /* ============================================================
     EMAIL MODAL
     ============================================================ */
  function openModal() {
    if (!modal) return;
    const form    = qs('.jf-modal__form',    modal);
    const success = qs('.jf-modal__success', modal);
    form?.classList.remove('is-hidden');
    success?.classList.remove('is-visible');
    const submit = qs('.jf-modal__submit', modal);
    if (submit) { submit.disabled = false; submit.textContent = 'Send My Results'; }

    modal.hidden = false;
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      setTimeout(() => qs('#jfModalName, #jfModalEmail', modal)?.focus(), 60);
    });

    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = sbw > 0 ? sbw + 'px' : '';
    document.body.style.overflow = 'hidden';
    enableFocusTrap(modal);
    track('email_modal_open');
  }

  function closeModal() {
    if (!modal) return;
    disableFocusTrap(modal);
    modal.classList.remove('is-open');
    document.body.style.paddingRight = '';
    document.body.style.overflow = '';
    setTimeout(() => { modal.hidden = true; }, 280);
    qs('[data-jf-email]', wrap)?.focus();
  }

  async function onEmailSubmit() {
    const nameInput  = qs('#jfModalName',  modal);
    const emailInput = qs('#jfModalEmail', modal);
    const submitBtn  = qs('.jf-modal__submit', modal);
    const form       = qs('.jf-modal__form',   modal);
    const success    = qs('.jf-modal__success', modal);

    const name  = nameInput?.value.trim()  || '';
    const email = emailInput?.value.trim() || '';

    if (!email || !email.includes('@')) {
      emailInput?.focus();
      emailInput?.classList.add('is-error');
      setTimeout(() => emailInput?.classList.remove('is-error'), 2000);
      return;
    }

    submitBtn.disabled    = true;
    submitBtn.textContent = 'Sending…';

    const prefs = buildPreferencesSummary();

    wrap.dispatchEvent(new CustomEvent('zizovJFLeadCapture', {
      bubbles: true,
      detail:  { name, email, preferences: prefs, results: generateShareUrl() }
    }));

    track('email_capture', { jf_preferences: prefs });

    try {
      const fd = new FormData();
      fd.append('form_type',        'contact');
      fd.append('utf8',             '✓');
      fd.append('contact[name]',    name || 'ZIZOV Jewellery Finder User');
      fd.append('contact[email]',   email);
      fd.append('contact[body]',
        `ZIZOV Jewellery Finder — Personalised Selection\n\nPreferences: ${prefs}\n\nResults: ${generateShareUrl()}`
      );
      await fetch('/contact', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
    } catch (_) {}

    form.classList.add('is-hidden');
    success.classList.add('is-visible');
    announce('Thank you! Check your inbox soon.');
  }


  /* ============================================================
     SAVE RESULTS — copy shareable URL to clipboard
     ============================================================ */
  function saveResults() {
    const url = generateShareUrl();
    const btn = qs('[data-jf-save]', wrap);
    if (!btn) return;

    navigator.clipboard.writeText(url).then(() => {
      const orig = btn.innerHTML;
      btn.innerHTML = btn.innerHTML.replace(/Save Results/, 'Link Copied!');
      btn.disabled = true;
      setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2400);
      track('save_results', { jf_method: 'clipboard' });
    }).catch(() => {
      // Fallback: select + copy via execCommand
      const el = document.createElement('textarea');
      el.value = url;
      el.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(el);
      const orig = btn.innerHTML;
      btn.innerHTML = btn.innerHTML.replace(/Save Results/, 'Link Copied!');
      btn.disabled = true;
      setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2400);
    });
  }


  /* ============================================================
     WHATSAPP CTA — pre-filled message with quiz answers
     ============================================================ */
  function generateWhatsappMessage() {
    const lines = ['Hi ZIZOV! 💎 I used the Jewellery Finder and found my preferences:'];
    const labelMap = {
      jewelleryType: 'Type',
      diamond:       'Diamond',
      occasion:      'Occasion',
      style:         'Style',
      shape:         'Shape'
    };
    Object.entries(labelMap).forEach(([key, display]) => {
      const val = state.answers[key];
      if (val && val !== '__skip' && LABELS[key]?.[val]) {
        lines.push(`• ${display}: ${LABELS[key][val]}`);
      }
    });
    const range = CFG.budgetRanges[state.answers.budgetIndex];
    if (range) lines.push(`• Budget: ${range.label}`);
    lines.push('');
    lines.push('Can you help me find the perfect piece? 🙏');
    return lines.join('\n');
  }

  function updateWhatsappLink() {
    const msg = generateWhatsappMessage();
    qsa('[data-jf-whatsapp]', wrap).forEach(btn => {
      const base = btn.dataset.jfWaBase || btn.getAttribute('href') || '';
      if (!base || base === '#') return;
      btn.href = `${base}?text=${encodeURIComponent(msg)}`;
    });
  }


  /* ============================================================
     BOOT
     ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
